import React, { useState, useRef, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import AlertBox from '../Components/AlertBox';
import Snackbar from '../Components/Snackbar';
import ConfirmDialog from '../Components/ConfirmDialog';
import { AUTH_API_BASE_URL as API_BASE_URL } from '../LeadMng/config/api';

const TaskForm = ({ onSubmit, onClose, initialData, userDepartments }) => {

  console.log(' TaskForm initialData:', initialData);
  console.log(' Recurring data received:', {
    recurringStartDate: initialData?.recurringStartDate,
    recurringEndDate: initialData?.recurringEndDate,
    repeatWeekdays: initialData?.repeatWeekdays,
    repeatMonthDays: initialData?.repeatMonthDays,
    repeat: initialData?.repeat,
    repeatFrequency: initialData?.repeatFrequency
  });

  // UPDATED: Conditionally generate taskId only for non-recurring tasks or existing edits
  const [taskId, setTaskId] = useState(initialData?.id || '');
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [assignees, setAssignees] = useState(initialData?.assignees || []);
  const [department, setDepartment] = useState(initialData?.department || '');
  const [priority, setPriority] = useState(initialData?.priority || 'Medium');

  const today = new Date().toISOString().split('T')[0];  // e.g., "2023-10-05"
  const currentTimeset = new Date().toTimeString().slice(0, 5);  // e.g., "14:30"
  const [currentTime, setCurrentTime] = useState(currentTimeset);


  const [startDate, setStartDate] = useState(initialData?.startDate || today)
  const [startTime, setStartTime] = useState(initialData?.startTime || '09:00');
  const [deadline, setDeadline] = useState(initialData?.deadline || today);
  const [endTime, setEndTime] = useState(initialData?.endTime || '17:00');

  // NEW: Next Follow-Up Date and Time fields
  const [nextFollowUpDate, setNextFollowUpDate] = useState(initialData?.nextFollowUpDate || today);
  const [nextFollowUpTime, setNextFollowUpTime] = useState(initialData?.nextFollowUpTime || '09:00');

  // NEW: Holiday-related states
  const [holidays, setHolidays] = useState([]);
  const [holidayError, setHolidayError] = useState('');
  const [loadingHolidays, setLoadingHolidays] = useState(false);

  const [repeat, setRepeat] = useState(initialData?.repeat || false);
  const [repeatFrequency, setRepeatFrequency] = useState(initialData?.repeatFrequency || 'daily');

  const [showAlert, setShowAlert] = useState(null);

  // UPDATED: Changed to arrays for multiple selections
  const [repeatWeekdays, setRepeatWeekdays] = useState(
    initialData?.repeatWeekdays && Array.isArray(initialData.repeatWeekdays)
      ? initialData.repeatWeekdays
      : initialData?.repeatWeekday
        ? [initialData.repeatWeekday]
        : []
  );
  const [repeatMonthDays, setRepeatMonthDays] = useState(
    initialData?.repeatMonthDays && Array.isArray(initialData.repeatMonthDays)
      ? initialData.repeatMonthDays
      : initialData?.repeatMonthDay
        ? [initialData.repeatMonthDay]
        : []
  );


  // NEW: Separate recurrence start and end dates
  const [recurringStartDate, setRecurringStartDate] = useState(initialData?.recurringStartDate || '');
  const [recurringEndDate, setRecurringEndDate] = useState(initialData?.recurringEndDate || '');

  const [repeatEndDate, setRepeatEndDate] = useState(initialData?.repeatEndDate || '');
  // Load attachments from all possible arrays (images, files, audios, attachments)
  const [attachments, setAttachments] = useState(() => {
    const loadedAttachments = [];
    if (initialData) {
      console.log('Initial attachment loading:', {
        images: initialData.images,
        audios: initialData.audios,
        files: initialData.files,
        attachments: initialData.attachments
      });

      // Helper to extract metadata from string or object
      const extractMeta = (item, fallbackType, idx) => {
        console.log(`Ž Processing ${fallbackType} item:`, item);

        if (typeof item === 'string') {
          const name = item.split('/').pop() || `Attachment ${idx + 1}`;
          const ext = name.split('.').pop()?.toLowerCase();
          let type = fallbackType;
          if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) type = 'image';
          else if (["mp3", "wav", "m4a", "ogg"].includes(ext)) type = 'audio';
          else if (["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"].includes(ext)) type = 'file';
          const result = {
            id: `${type}-${Date.now()}-${idx}`,
            name,
            type,
            size: 0,
            url: item,
            uploadDate: initialData.createdAt || new Date().toISOString()
          };
          console.log(`✅ Extracted from string:`, result);
          return result;
        } else if (typeof item === 'object' && item !== null) {
          // Extract URL from object - handle both direct url and nested structure
          const url = item.url || item.path || item.secure_url;
          if (!url) {
            console.warn('⚠️ No URL found in attachment object:', item);
            return null;
          }

          // Determine type and extension - prefer explicit type field, fall back to extension
          let type = fallbackType;
          let fileExtension = '';

          if (item.type) {
            // Handle MIME types (e.g., "image/jpeg" -> "image")
            if (item.type.startsWith('image/')) {
              type = 'image';
              const mimeExt = item.type.split('/')[1];
              if (mimeExt === 'jpeg') fileExtension = '.jpg';
              else if (mimeExt) fileExtension = `.${mimeExt}`;
            } else if (item.type.startsWith('audio/')) {
              type = 'audio';
              const mimeExt = item.type.split('/')[1];
              if (mimeExt) fileExtension = `.${mimeExt}`;
            } else if (item.type.startsWith('application/') || item.type.startsWith('text/')) {
              type = 'file';
              const mimeExt = item.type.split('/')[1];
              if (mimeExt === 'pdf') fileExtension = '.pdf';
              else if (mimeExt === 'msword') fileExtension = '.doc';
              else if (mimeExt === 'vnd.openxmlformats-officedocument.wordprocessingml.document') fileExtension = '.docx';
              else if (mimeExt === 'vnd.ms-excel') fileExtension = '.xls';
              else if (mimeExt === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') fileExtension = '.xlsx';
              else if (mimeExt === 'plain') fileExtension = '.txt';
              else if (mimeExt) fileExtension = `.${mimeExt.split('.').pop()}`;
            } else if (item.type === 'image' || item.type === 'audio' || item.type === 'file') {
              type = item.type;
            }
          } else {
            // Fallback to extension-based detection
            const ext = url.split('.').pop()?.toLowerCase();
            if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) {
              type = 'image';
              fileExtension = `.${ext}`;
            } else if (["mp3", "wav", "m4a", "ogg"].includes(ext)) {
              type = 'audio';
              fileExtension = `.${ext}`;
            } else {
              type = 'file';
              if (ext && ext.length <= 5) fileExtension = `.${ext}`;
            }
          }

          // Get the base name and ensure it has proper extension
          let fileName = item.name || item.filename || item.originalname || url.split('/').pop() || `Attachment_${idx + 1}`;

          // If filename doesn't have extension and we determined one, add it
          if (fileExtension && !fileName.toLowerCase().includes('.')) {
            fileName = fileName + fileExtension;
          } else if (fileExtension && fileName.includes('.')) {
            // Check if the extension matches what we expect
            const currentExt = '.' + fileName.split('.').pop().toLowerCase();
            if (currentExt !== fileExtension.toLowerCase() && currentExt.length > 5) {
              // Replace weird extension with proper one
              fileName = fileName.substring(0, fileName.lastIndexOf('.')) + fileExtension;
            }
          }

          const result = {
            id: item._id || item.id || `${type}-${Date.now()}-${idx}`,
            name: fileName,
            type: type,
            size: item.size || 0,
            url: url,
            uploadDate: item.uploadDate || item.createdAt || initialData.createdAt || new Date().toISOString()
          };
          console.log(`✅ Extracted from object:`, result);
          return result;
        }
        return null;
      };
      if (initialData.images && Array.isArray(initialData.images)) {
        initialData.images.forEach((img, idx) => {
          const meta = extractMeta(img, 'image', idx);
          if (meta) loadedAttachments.push(meta);
        });
      }
      if (initialData.audios && Array.isArray(initialData.audios)) {
        initialData.audios.forEach((aud, idx) => {
          const meta = extractMeta(aud, 'audio', idx);
          if (meta) loadedAttachments.push(meta);
        });
      }
      if (initialData.files && Array.isArray(initialData.files)) {
        initialData.files.forEach((file, idx) => {
          const meta = extractMeta(file, 'file', idx);
          if (meta) loadedAttachments.push(meta);
        });
      }
      if (initialData.attachments && Array.isArray(initialData.attachments)) {
        initialData.attachments.forEach((att, idx) => {
          const meta = extractMeta(att, 'attachment', idx);
          if (meta) loadedAttachments.push(meta);
        });
      }
    }
    return loadedAttachments;
  });
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info'
  });

  // Confirm Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null
  });

  const [dialogLoading, setDialogLoading] = useState(false);

  // Reset form fields when initialData changes (for edit mode)
  useEffect(() => {
    setTaskId(initialData?.id || '');
    setTitle(initialData?.title || '');
    setDescription(initialData?.description || '');
    setAssignees(initialData?.assignees || []);
    setDepartment(initialData?.department || '');
    setPriority(initialData?.priority || 'Medium');
    setStartDate(initialData?.startDate || today);
    setStartTime(initialData?.startTime || '09:00');
    setDeadline(initialData?.deadline || today);
    setEndTime(initialData?.endTime || '17:00');
    setNextFollowUpDate(initialData?.nextFollowUpDate || today);
    setNextFollowUpTime(initialData?.nextFollowUpTime || '09:00');
    setRepeat(initialData?.repeat || false);
    setRepeatFrequency(initialData?.repeatFrequency || 'daily');

    const weekdaysToSet = initialData?.repeatWeekdays && Array.isArray(initialData.repeatWeekdays)
      ? initialData.repeatWeekdays
      : initialData?.repeatWeekday
        ? [initialData.repeatWeekday]
        : [];
    console.log('🔧 useEffect setting repeatWeekdays to:', weekdaysToSet);
    setRepeatWeekdays(weekdaysToSet);

    const monthDaysToSet = initialData?.repeatMonthDays && Array.isArray(initialData.repeatMonthDays)
      ? initialData.repeatMonthDays
      : initialData?.repeatMonthDay
        ? [initialData.repeatMonthDay]
        : [];
    console.log('🔧 useEffect setting repeatMonthDays to:', monthDaysToSet);
    setRepeatMonthDays(monthDaysToSet);

    setRecurringStartDate(initialData?.recurringStartDate || '');
    setRecurringEndDate(initialData?.recurringEndDate || '');
    setRepeatEndDate(initialData?.repeatEndDate || '');
    setAttachments(() => {
      const loadedAttachments = [];
      if (initialData) {
        console.log('„ Re-loading attachments from initialData change');

        // Helper to extract metadata from string or object
        const extractMeta = (item, fallbackType, idx) => {
          if (typeof item === 'string') {
            const name = item.split('/').pop() || `Attachment ${idx + 1}`;
            const ext = name.split('.').pop()?.toLowerCase();
            let type = fallbackType;
            if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) type = 'image';
            else if (["mp3", "wav", "m4a", "ogg"].includes(ext)) type = 'audio';
            else if (["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"].includes(ext)) type = 'file';
            return {
              id: `${type}-${Date.now()}-${idx}`,
              name,
              type,
              size: 0,
              url: item,
              uploadDate: initialData.createdAt || new Date().toISOString()
            };
          } else if (typeof item === 'object' && item !== null) {
            // Extract URL from object - handle both direct url and nested structure
            const url = item.url || item.path || item.secure_url;
            if (!url) {
              console.warn('⚠️ No URL found in attachment object:', item);
              return null;
            }

            // Determine type and extension - prefer explicit type field, fall back to extension
            let type = fallbackType;
            let fileExtension = '';

            if (item.type) {
              // Handle MIME types (e.g., "image/jpeg" -> "image")
              if (item.type.startsWith('image/')) {
                type = 'image';
                const mimeExt = item.type.split('/')[1];
                if (mimeExt === 'jpeg') fileExtension = '.jpg';
                else if (mimeExt) fileExtension = `.${mimeExt}`;
              } else if (item.type.startsWith('audio/')) {
                type = 'audio';
                const mimeExt = item.type.split('/')[1];
                if (mimeExt) fileExtension = `.${mimeExt}`;
              } else if (item.type.startsWith('application/') || item.type.startsWith('text/')) {
                type = 'file';
                const mimeExt = item.type.split('/')[1];
                if (mimeExt === 'pdf') fileExtension = '.pdf';
                else if (mimeExt === 'msword') fileExtension = '.doc';
                else if (mimeExt === 'vnd.openxmlformats-officedocument.wordprocessingml.document') fileExtension = '.docx';
                else if (mimeExt === 'vnd.ms-excel') fileExtension = '.xls';
                else if (mimeExt === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') fileExtension = '.xlsx';
                else if (mimeExt === 'plain') fileExtension = '.txt';
                else if (mimeExt) fileExtension = `.${mimeExt.split('.').pop()}`;
              } else if (item.type === 'image' || item.type === 'audio' || item.type === 'file') {
                type = item.type;
              }
            } else {
              // Fallback to extension-based detection
              const ext = url.split('.').pop()?.toLowerCase();
              if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) {
                type = 'image';
                fileExtension = `.${ext}`;
              } else if (["mp3", "wav", "m4a", "ogg"].includes(ext)) {
                type = 'audio';
                fileExtension = `.${ext}`;
              } else {
                type = 'file';
                if (ext && ext.length <= 5) fileExtension = `.${ext}`;
              }
            }

            // Get the base name and ensure it has proper extension
            let fileName = item.name || item.filename || item.originalname || url.split('/').pop() || `Attachment_${idx + 1}`;

            // If filename doesn't have extension and we determined one, add it
            if (fileExtension && !fileName.toLowerCase().includes('.')) {
              fileName = fileName + fileExtension;
            } else if (fileExtension && fileName.includes('.')) {
              // Check if the extension matches what we expect
              const currentExt = '.' + fileName.split('.').pop().toLowerCase();
              if (currentExt !== fileExtension.toLowerCase() && currentExt.length > 5) {
                // Replace weird extension with proper one
                fileName = fileName.substring(0, fileName.lastIndexOf('.')) + fileExtension;
              }
            }

            return {
              id: item._id || item.id || `${type}-${Date.now()}-${idx}`,
              name: fileName,
              type: type,
              size: item.size || 0,
              url: url,
              uploadDate: item.uploadDate || item.createdAt || initialData.createdAt || new Date().toISOString()
            };
          }
          return null;
        };
        if (initialData.images && Array.isArray(initialData.images)) {
          initialData.images.forEach((img, idx) => {
            const meta = extractMeta(img, 'image', idx);
            if (meta) loadedAttachments.push(meta);
          });
        }
        if (initialData.audios && Array.isArray(initialData.audios)) {
          initialData.audios.forEach((aud, idx) => {
            const meta = extractMeta(aud, 'audio', idx);
            if (meta) loadedAttachments.push(meta);
          });
        }
        if (initialData.files && Array.isArray(initialData.files)) {
          initialData.files.forEach((file, idx) => {
            const meta = extractMeta(file, 'file', idx);
            if (meta) loadedAttachments.push(meta);
          });
        }
        if (initialData.attachments && Array.isArray(initialData.attachments)) {
          initialData.attachments.forEach((att, idx) => {
            const meta = extractMeta(att, 'attachment', idx);
            if (meta) loadedAttachments.push(meta);
          });
        }
      }
      return loadedAttachments;
    });
  }, [initialData]);

  // Debug: Log repeatWeekdays whenever it changes
  useEffect(() => {
    console.log('„ repeatWeekdays state changed:', repeatWeekdays);
  }, [repeatWeekdays]);

  useEffect(() => {
    console.log('„ repeatMonthDays state changed:', repeatMonthDays);
  }, [repeatMonthDays]);

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // NEW: States for tracking removals (only for existing server attachments)

  const [removedImages, setRemovedImages] = useState([]);  // Array of deleted image URLs
  const [removedAudios, setRemovedAudios] = useState([]);
  const [removedFiles, setRemovedFiles] = useState([]);
  // API data states
  const [departments, setDepartments] = useState([]);
  const [departmentObjects, setDepartmentObjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const formRef = useRef(null);
  // const [taskPoints, setTaskPoints] = useState(0);
  const [accessPermissions, setAccessPermissions] = useState([]);

  // API base URL

  // Weekdays for weekly repetition
  const weekdays = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Days of month for monthly repetition
  const monthDays = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

  // Priority options with colors matching the design
  const priorities = [
    { value: 'Low', label: 'Low', color: '#00E396' },
    { value: 'Medium', label: 'Medium', color: '#FEB019' },
    { value: 'High', label: 'High', color: '#FF4560' },
  ];

  // Enhanced authentication token retrieval
  const getAuthToken = () => {
    const possibleKeys = [
      'authToken',
      'authToken',
      'token',
      'jwt',
      'bearerToken',
      'userToken'
    ];

    let token = null;

    for (const key of possibleKeys) {
      token = localStorage.getItem(key);
      if (token) {
        token = token.replace(/['"]/g, '').trim();
        break;
      }
    }

    if (!token) {
      console.warn('⚠️ No authentication token found in localStorage');
    }

    return token;
  };

  // Get company ID
  const getCompanyId = useCallback(async () => {

    const token = localStorage.getItem('authToken');

    if (token) {
      try {
        const decodedToken = jwtDecode(token);

        // New logic based on role
        if (decodedToken && decodedToken.role === 'company') {
          // If role is company, use id as companyId
          if (decodedToken.id) {
            console.groupEnd();
            return decodedToken.id;
          }
        } else {
          // Otherwise, use companyId field if available
          if (decodedToken && decodedToken.companyId) {
            console.groupEnd();
            return decodedToken.companyId;
          }
        }

        // If role is not company and companyId is not present, try userId/id to fetch profile
        if (decodedToken && (decodedToken.userId || decodedToken.id)) {
          const userId = decodedToken.userId || decodedToken.id;
        } else {
          console.warn('⚠️ No userId or id found in JWT token');
        }
      } catch (error) {
        console.error('❌ JWT decoding failed:', error);
        console.warn('„ Trying alternative methods...');
      }
    } else {
      console.warn('⚠️ No authToken found in localStorage');
    }

    // Fallback: try localStorage
    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId) {
      console.groupEnd();
      return storedCompanyId;
    } else {
      console.warn('⚠️ No companyId found in localStorage');
    }

    console.error('❌ Unable to resolve company ID from any source');
    console.groupEnd();
    return null;
  }, []);

  // ENHANCED: Fetch individual employee details including shift timing details
  const fetchEmployeeDetailsWithShift = async (employeeId) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const url = `${API_BASE_URL}/employees/${employeeId}`;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        'x-access-token': token
      };


      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch employee details: ${response.status}`);
      }

      const data = await response.json();

      // Handle different API response structures
      let employeeData = data;
      if (data.data) {
        employeeData = data.data;
      } else if (data.employee) {
        employeeData = data.employee;
      }

      // If employee has a shift ID, fetch the actual shift details
      if (employeeData.shift && typeof employeeData.shift === 'string') {
        const shiftDetails = await fetchShiftDetails(employeeData.shift);

        if (shiftDetails) {
          // Merge shift timing into employee details
          employeeData.shiftDetails = {
            id: shiftDetails._id || shiftDetails.id,
            name: shiftDetails.name || shiftDetails.shiftName || 'Unknown Shift',
            startTime: shiftDetails.startTime,
            endTime: shiftDetails.endTime,
            duration: shiftDetails.duration,
            description: shiftDetails.description,
            createdBy: shiftDetails.createdBy,
          };

        } else {
          console.warn(`⚠️ Could not fetch shift details for shift ID: ${employeeData.shift}`);
        }
      }

      return employeeData;

    } catch (error) {
      console.error('Enhanced employee details fetch failed:', error);
      return null;
    }
  };

  // NEW: Fetch shift details from shift ID
  const fetchShiftDetails = async (shiftId) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const url = `${API_BASE_URL}/shifts/${shiftId}`;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        'x-access-token': token
      };


      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch shift details: ${response.status}`);
      }

      const data = await response.json();

      // Handle different API response structures
      let shiftData = data;
      if (data.data) {
        shiftData = data.data;
      } else if (data.shift) {
        shiftData = data.shift;
      }

      return shiftData;

    } catch (error) {
      console.error('Shift details fetch failed:', error);
      return null;
    }
  };

  useEffect(() => {
    const autoFillShiftTimes = async () => {
      // Only auto-fill if:
      // 1. At least one employee is selected
      // 2. We're not editing an existing task (initialData check)
      if (selectedEmployees.length > 0 && !initialData?.id) {
        try {
          // Fetch the first selected employee's details with shift
          const firstEmployee = selectedEmployees[0];
          const employeeDetails = await fetchEmployeeDetailsWithShift(
            firstEmployee._id || firstEmployee.id
          );

          if (employeeDetails?.shiftDetails) {
            const { startTime: shiftStart, endTime: shiftEnd } = employeeDetails.shiftDetails;

            // Helper: convert "7:00 PM" or "7:00 AM" to "19:00" / "07:00" (HH:mm)
            const to24HourStr = (t) => {
              if (!t) return '';
              const s = t.toString().trim();
              const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm)?$/);
              if (!m) return s; // already HH:mm or unknown
              let h = parseInt(m[1], 10);
              const min = m[2];
              const period = (m[3] || '').toUpperCase();
              if (period === 'PM' && h !== 12) h += 12;
              if (period === 'AM' && h === 12) h = 0;
              return `${String(h).padStart(2, '0')}:${min}`;
            };

            // Only auto-fill if shift times exist
            if (shiftStart && shiftEnd) {
              const start24 = to24HourStr(shiftStart);
              const end24 = to24HourStr(shiftEnd);
              // Set the start, end, AND next follow-up times to match the shift
              setStartTime(start24);
              setEndTime(end24);
              setNextFollowUpTime(start24); // NEW: Auto-fill next follow-up with shift start time

              console.log(`✅ Auto-filled all times from ${firstEmployee.teamMemberName || firstEmployee.name}'s shift:`);
              console.log(`   Start Time: ${start24} (original: ${shiftStart})`);
              console.log(`   End Time: ${end24} (original: ${shiftEnd})`);
              console.log(`   Next Follow-Up Time: ${start24}`);
            }
          }
        } catch (error) {
          console.error('Failed to auto-fill shift times:', error);
          // Don't show error to user - keep default times
        }
      }
    };

    autoFillShiftTimes();
  }, [selectedEmployees]); // Trigger when selectedEmployees changes

  // ENHANCED: Helper function to convert time to 24-hour format for comparison
  const convertTo24Hour = (timeStr) => {
    if (!timeStr) return 0;

    // Handle different time formats
    let time = timeStr.toString().trim();

    // Check if it already contains AM/PM
    const periodMatch = time.match(/\s*(AM|PM|am|pm)\s*$/);
    let period = periodMatch ? periodMatch[1].toUpperCase() : null;

    // Remove period from time string
    if (period) {
      time = time.replace(/\s*(AM|PM|am|pm)\s*$/, '');
    }

    // Split hours and minutes
    const [hoursStr, minutesStr] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr || '0', 10);

    // Handle 12-hour format conversion
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    // If no period specified, assume 24-hour format
    // Convert to comparable number (e.g., 14:30 = 1430)
    return hours * 100 + minutes;
  };


  // ENHANCED: Validate task assignment against employee schedule with actual shift timing
  const validateEmployeeSchedule = async (selectedEmployees, startDate, startTime, endTime) => {
    const violations = [];

    // Helper function to get day name safely
    const getDayName = (date) => {
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      return days[date.getDay()];
    };

    // Helper function to normalize day names for comparison
    const normalizeDayName = (dayName) => {
      const dayMap = {
        'sun': 'sunday', 'sunday': 'sunday',
        'mon': 'monday', 'monday': 'monday',
        'tue': 'tuesday', 'tuesday': 'tuesday',
        'wed': 'wednesday', 'wednesday': 'wednesday',
        'thu': 'thursday', 'thursday': 'thursday',
        'fri': 'friday', 'friday': 'friday',
        'sat': 'saturday', 'saturday': 'saturday'
      };
      return dayMap[dayName.toLowerCase()] || dayName.toLowerCase();
    };

    for (const employee of selectedEmployees) {
      try {
        // Use enhanced function that includes shift details
        const employeeDetails = await fetchEmployeeDetailsWithShift(employee._id || employee.id);

        if (!employeeDetails) {
          violations.push(`Unable to fetch schedule for ${employee.teamMemberName || employee.name}`);
          continue;
        }

        // Check weekly holidays - handle both singular and plural field names
        const taskDate = new Date(startDate);
        const taskDayName = getDayName(taskDate);


        // Check both possible field names: weeklyHoliday (singular) and weeklyHolidays (plural)
        const weeklyHolidays = employeeDetails.weeklyHolidays || employeeDetails.weeklyHoliday || [];


        if (Array.isArray(weeklyHolidays) && weeklyHolidays.length > 0) {
          const hasWeeklyHoliday = weeklyHolidays.some(holiday => {
            const normalizedHoliday = normalizeDayName(holiday);
            return normalizedHoliday === taskDayName;
          });

          if (hasWeeklyHoliday) {
            violations.push(`${employee.teamMemberName || employee.name} has weekly holiday on ${taskDayName}s`);
          }
        }

        // ENHANCED: Check shift timing using actual shift details from API
        if (employeeDetails.shiftDetails) {
          const { startTime: shiftStart, endTime: shiftEnd, name: shiftName } = employeeDetails.shiftDetails;

          if (shiftStart && shiftEnd) {
            try {
              // Convert times to comparable format (24-hour)
              const taskStartTime = convertTo24Hour(startTime);
              const taskEndTime = convertTo24Hour(endTime);
              const employeeShiftStart = convertTo24Hour(shiftStart);
              const employeeShiftEnd = convertTo24Hour(shiftEnd);

              // Handle night shifts that cross midnight
              if (employeeShiftEnd < employeeShiftStart) {
                // Night shift (e.g., 23:00 to 08:00)
                const isTaskInNightShift = (taskStartTime >= employeeShiftStart) || (taskEndTime <= employeeShiftEnd);
                if (!isTaskInNightShift) {
                  violations.push(`Task timing (${startTime}-${endTime}) is outside ${employee.teamMemberName || employee.name}'s ${shiftName} shift (${shiftStart}-${shiftEnd})`);
                }
              } else {
                // Regular shift within same day
                if (taskStartTime < employeeShiftStart || taskEndTime > employeeShiftEnd) {
                  violations.push(`Task timing (${startTime}-${endTime}) is outside ${employee.teamMemberName || employee.name}'s ${shiftName} shift (${shiftStart}-${shiftEnd})`);
                }
              }
            } catch (timeError) {
              console.error('Time conversion error:', timeError);
              violations.push(`Unable to validate shift timing for ${employee.teamMemberName || employee.name} - invalid time format`);
            }
          } else {
            violations.push(`${employee.teamMemberName || employee.name} has incomplete shift timing information`);
          }
        } else if (employeeDetails.shift) {
          // Fallback: if shift ID exists but we couldn't fetch details
          violations.push(`Unable to fetch shift details for ${employee.teamMemberName || employee.name} (Shift ID: ${employeeDetails.shift})`);
        } else {
          violations.push(`${employee.teamMemberName || employee.name} has no shift assigned`);
        }

      } catch (error) {
        console.error('Employee schedule validation error:', error);
        violations.push(`Error validating schedule for ${employee.teamMemberName || employee.name}: ${error.message}`);
      }
    }

    return violations;
  };






  // NEW: Fetch holidays from API
  const fetchHolidays = async () => {
    try {
      setLoadingHolidays(true);
      const companyId = await getCompanyId();
      const token = getAuthToken();

      if (!companyId) {
        throw new Error("No company ID found for fetching holidays");
      }

      if (!token) {
        throw new Error("No authentication token found for fetching holidays");
      }

      const url = `https://slateblue-mallard-430836.hostingersite.com/api/holidaycreation/company/${companyId}`;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        'x-access-token': token
      };

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (response.status === 401) {
        throw new Error("Authentication failed while fetching holidays");
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch holidays: ${response.status}`);
      }

      const data = await response.json();

      let holidayList = [];
      if (Array.isArray(data)) {
        holidayList = data;
      } else if (data && Array.isArray(data.holidays)) {
        holidayList = data.holidays;
      } else if (data && Array.isArray(data.data)) {
        holidayList = data.data;
      }

      // Store full holiday objects with date ranges
      const holidayRanges = holidayList.map(holiday => ({
        name: holiday.name || holiday.holidayName,
        startDate: holiday.startDate,
        endDate: holiday.endDate,
        reason: holiday.reason
      }));

      console.log('🎉 Holidays loaded successfully:', {
        count: holidayRanges.length,
        holidays: holidayRanges
      });

      setHolidays(holidayRanges);
      return holidayRanges;
    } catch (error) {
      console.error('❌ Holiday fetch failed:', error);
      setHolidayError(error.message);
      setHolidays([]);
      return [];
    } finally {
      setLoadingHolidays(false);
    }
  };

  // NEW: Check if a date falls within any holiday range
  const isHoliday = (dateString) => {
    if (!dateString || holidays.length === 0) {
      return false;
    }

    // Parse the date string as local date (YYYY-MM-DD format)
    const [year, month, day] = dateString.split('-').map(Number);
    const checkDate = new Date(year, month - 1, day); // Local date
    checkDate.setHours(0, 0, 0, 0);

    const result = holidays.some(holiday => {
      // Parse holiday dates as local dates
      const startParts = holiday.startDate.split('T')[0].split('-').map(Number);
      const holidayStart = new Date(startParts[0], startParts[1] - 1, startParts[2]);
      holidayStart.setHours(0, 0, 0, 0);

      const endParts = holiday.endDate.split('T')[0].split('-').map(Number);
      const holidayEnd = new Date(endParts[0], endParts[1] - 1, endParts[2]);
      holidayEnd.setHours(0, 0, 0, 0);

      return checkDate >= holidayStart && checkDate <= holidayEnd;
    });

    return result;
  };

  // NEW: Get previous working date (skip weekends and holidays)
  const getPreviousWorkingDate = (dateString) => {
    if (!dateString) return dateString;

    // Parse as local date
    const [year, month, day] = dateString.split('-').map(Number);
    let currentDate = new Date(year, month - 1, day);
    currentDate.setHours(0, 0, 0, 0);

    // Keep going back until we find a working day
    let attempts = 0;
    const maxAttempts = 60; // Prevent infinite loop (2 months worth of days)

    while (attempts < maxAttempts) {
      // Move to previous day
      currentDate.setDate(currentDate.getDate() - 1);

      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
      // Format as YYYY-MM-DD in local timezone
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

      console.log(`„ Checking previous date: ${dateStr}, dayOfWeek: ${dayOfWeek}, isHoliday: ${isHoliday(dateStr)}`);

      // Check if it's a working day (not weekend and not holiday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday(dateStr)) {
        console.log(`✅ Found previous working date: ${dateStr}`);
        return dateStr;
      }

      attempts++;
    }

    console.warn('⚠️ Could not find a working date within 60 days');
    return dateString; // Return original if no working date found
  };

  // NEW: Automatically shift start date if it falls on a holiday
  const handleStartDateChange = (newDate) => {
    if (!newDate) {
      setStartDate(newDate);
      return;
    }

    // Just set the date without validation - will check on submit
    setStartDate(newDate);

    // Clear any start date errors
    if (formErrors.startDate) {
      setFormErrors({ ...formErrors, startDate: '' });
    }
  };

  // NEW: Validate date against holidays - Now just logs, doesn't block
  const validateDateAgainstHolidays = (date, fieldName) => {
    if (!date) return true;

    if (isHoliday(date)) {
      console.log(`⚠️ ${fieldName} falls on a holiday but will proceed with submission`);
      // Don't set form errors since we auto-shift the date
      return true;
    }

    // Clear holiday error if date is valid
    setFormErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[fieldName]?.includes('holiday')) {
        delete newErrors[fieldName];
      }
      return newErrors;
    });
    return true;
  };

  // NEW: Automatically shift deadline date if it falls on a holiday
  const handleDeadlineChange = (newDate) => {
    if (!newDate) {
      setDeadline(newDate);
      return;
    }

    // Just set the date without validation - will check on submit
    setDeadline(newDate);

    // Clear any deadline errors
    if (formErrors.deadline) {
      setFormErrors({ ...formErrors, deadline: '' });
    }
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const decoded = jwtDecode(token);
      setAccessPermissions(decoded.accessPermissions || []);
      setUserRole(decoded.role || null);
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
  }, []);

  // NEW: Load holidays on component mount
  useEffect(() => {
    const loadHolidays = async () => {
      await fetchHolidays();
    };

    loadHolidays();
  }, []);

  // UPDATED: Dynamic taskId generation based on repeat state
  useEffect(() => {
    if (initialData?.id) {
      // For editing existing tasks, use the provided ID
      setTaskId(initialData.id);
    } else if (!repeat) {
      // For new non-recurring tasks, generate immediately
      setTaskId(`TASK-${Date.now()}`);
    } else {
      // For new recurring tasks, don't generate yet (leave empty or set placeholder)
      setTaskId(''); // Or use a string like 'To be assigned' for clarity
    }
  }, [repeat, initialData]); // Re-run when repeat toggles or initialData changes

  // Enhanced department fetching to get full department objects
  const fetchCompanyDepartments = async () => {
    try {
      const companyId = await getCompanyId();
      const token = getAuthToken();

      if (!companyId) {
        throw new Error("No company ID found. Please log in again.");
      }

      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      const url = `${API_BASE_URL}/departments/company/${companyId}`;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        'x-access-token': token
      };

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (response.status === 401) {
        throw new Error("Authentication failed. Please log in again.");
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch departments: ${response.status}`);
      }

      const data = await response.json();

      let departmentObjects = [];
      let departmentNames = [];

      if (Array.isArray(data)) {
        departmentObjects = data;
        departmentNames = data.map(dept => ({
          id: dept._id,
          name: dept.name || dept.departmentName,
          display: `${dept.name || dept.departmentName} (${dept._id})`
        }));
      } else if (data && Array.isArray(data.departments)) {
        departmentObjects = data.departments;
        departmentNames = data.departments.map(dept => ({
          id: dept._id,
          name: dept.name || dept.departmentName,
          display: `${dept.name || dept.departmentName} (${dept._id})`
        }));
      } else if (data && Array.isArray(data.data)) {
        departmentObjects = data.data;
        departmentNames = data.data.map(dept => ({
          id: dept._id,
          name: dept.name || dept.departmentName,
          display: `${dept.name || dept.departmentName} (${dept._id})`
        }));
      }

      // Filter out any invalid departments
      const validDepartments = departmentNames.filter(dept => dept.name && dept.id);

      setDepartmentObjects(departmentObjects);
      setDepartments(validDepartments.length ? validDepartments : [
        { id: '1', name: 'Engineering', display: 'Engineering (1)' },
        { id: '2', name: 'Marketing', display: 'Marketing (2)' },
        { id: '3', name: 'Sales', display: 'Sales (3)' }
      ]);

      return departmentNames;
    } catch (error) {
      console.error('Department fetch failed:', error);
      setApiError(error.message);
      setDepartments(['Engineering', 'Marketing', 'Sales']);
      return [];
    }
  };

  // Enhanced employee fetching with department filtering - UPDATED for department arrays
  // const fetchDepartmentEmployees = async (selectedDepartmentId) => {
  //   try {
  //     const companyId = await getCompanyId();
  //     const token = getAuthToken();

  //     if (!companyId) {
  //       throw new Error("No company ID found. Please log in again.");
  //     }

  //     if (!token) {
  //       throw new Error("No authentication token found. Please log in again.");
  //     }

  //     const url = `${API_BASE_URL}/employees/company/${companyId}`;
  //     const headers = { 
  //       'Content-Type': 'application/json',
  //       'Accept': 'application/json',
  //       'Authorization': `Bearer ${token}`,
  //       'x-auth-token': token,
  //       'x-access-token': token
  //     };

  //     const response = await fetch(url, {
  //       method: 'GET',
  //       headers: headers,
  //       credentials: 'include'
  //     });

  //     if (response.status === 401) {
  //       throw new Error("Authentication failed. Please log in again.");
  //     }

  //     if (!response.ok) {
  //       const errorText = await response.text();
  //       throw new Error(`Failed to fetch employees: ${response.status} - ${errorText}`);
  //     }

  //     const data = await response.json();

  //     let employeeList = [];
  //     if (Array.isArray(data)) {
  //       employeeList = data;
  //     } else if (data && Array.isArray(data.employees)) {
  //       employeeList = data.employees;
  //     } else if (data && Array.isArray(data.data)) {
  //       employeeList = data.data;
  //     }


  //     // Filter employees by department ID if selected
  //     if (selectedDepartmentId && selectedDepartmentId.trim()) {
  //       // First try to find the department in the departmentObjects
  //       const selectedDept = departmentObjects.find(dept => 
  //         dept._id === selectedDepartmentId || dept.id === selectedDepartmentId
  //       );


  //       employeeList = employeeList.filter(employee => {
  //         const empDepartment = employee.department;
  //         const empDepartmentName = employee.departmentName;

  //         // NEW: Handle department as array
  //         if (Array.isArray(empDepartment)) {
  //           // Check if selectedDepartmentId is in the department array
  //           const isInDepartmentArray = empDepartment.some(dept => {
  //             if (typeof dept === 'object' && dept !== null) {
  //               return dept._id === selectedDepartmentId || dept.id === selectedDepartmentId;
  //             } else {
  //               return dept === selectedDepartmentId;
  //             }
  //           });

  //           if (isInDepartmentArray) {
  //             return true;
  //           }
  //         }
  //         // Handle department as object (backward compatibility)
  //         else if (typeof empDepartment === 'object' && empDepartment !== null) {
  //           const empDepartmentId = empDepartment._id || empDepartment.id;
  //           if (empDepartmentId === selectedDepartmentId) {
  //             return true;
  //           }
  //         }
  //         // Handle department as string (backward compatibility)
  //         else if (typeof empDepartment === 'string') {
  //           if (empDepartment === selectedDepartmentId) {
  //             return true;
  //           }
  //         }

  //         // If we have the department object, also match by name as fallback
  //         if (selectedDept && (empDepartmentName === (selectedDept.name || selectedDept.departmentName))) {
  //           return true;
  //         }

  //         return false;
  //       });
  //     }

  //     setEmployees(employeeList);
  //     return employeeList;
  //   } catch (error) {
  //     console.error('Employee fetch failed:', error);
  //     setApiError(error.message);
  //     setEmployees([]);
  //     return [];
  //   }
  // };

  const fetchDepartmentEmployees = async (selectedDepartmentId) => {
    try {
      const companyId = await getCompanyId();
      const token = getAuthToken();

      if (!companyId) throw new Error("No company ID found. Please log in again.");
      if (!token) throw new Error("No authentication token found. Please log in again.");

      const url = `${API_BASE_URL}/employees/company/${companyId}`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "x-auth-token": token,
        "x-access-token": token,
      };

      const response = await fetch(url, {
        method: "GET",
        headers: headers,
        credentials: "include",
      });

      if (response.status === 401)
        throw new Error("Authentication failed. Please log in again.");
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch employees: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      let employeeList = [];

      if (Array.isArray(data)) {
        employeeList = data;
      } else if (data && Array.isArray(data.employees)) {
        employeeList = data.employees;
      } else if (data && Array.isArray(data.data)) {
        employeeList = data.data;
      }

      // ✅ Filter out inactive employees FIRST
      employeeList = employeeList.filter(
        (employee) => employee.isActive === true || employee.isActive === undefined
      );

      // Filter employees by department ID if selected
      if (selectedDepartmentId && selectedDepartmentId.trim()) {
        const selectedDept = departmentObjects.find(
          (dept) => dept.id === selectedDepartmentId || dept._id === selectedDepartmentId
        );

        employeeList = employeeList.filter((employee) => {
          const empDepartment = employee.department;
          const empDepartmentName = employee.departmentName;

          // Handle department as array
          if (Array.isArray(empDepartment)) {
            const isInDepartmentArray = empDepartment.some((dept) => {
              if (typeof dept === "object" && dept !== null) {
                return dept.id === selectedDepartmentId || dept._id === selectedDepartmentId;
              } else {
                return dept === selectedDepartmentId;
              }
            });
            if (isInDepartmentArray) return true;
          }
          // Handle department as object
          else if (typeof empDepartment === "object" && empDepartment !== null) {
            const empDepartmentId = empDepartment.id || empDepartment._id;
            if (empDepartmentId === selectedDepartmentId) return true;
          }
          // Handle department as string
          else if (typeof empDepartment === "string") {
            if (empDepartment === selectedDepartmentId) return true;
          }

          // Match by name as fallback
          if (
            selectedDept &&
            empDepartmentName === (selectedDept.name || selectedDept.departmentName)
          ) {
            return true;
          }

          return false;
        });
      }

      setEmployees(employeeList.filter(emp => emp.isActive !== false));
      return employeeList.filter(emp => emp.isActive !== false);
    } catch (error) {
      console.error("Employee fetch failed:", error);
      setApiError(error.message);
      setEmployees([]);
      return [];
    }
  };


  /**
   * Create or Update Task using the API - UPDATED for multiple assignees and next follow-up
   * @param {Object} taskData - Task data to be submitted
   * @returns {Promise<Object>} The created/updated task
   */
  const submitTask = async (taskData) => {

    try {
      setIsSubmitting(true);
      setApiError(null);
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      // Normalize helper: extract YYYY-MM-DD from any date string (ISO or plain)
      const toDateString = (d) => {
        if (!d) return null;
        if (typeof d === 'string') {
          if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
          const parsed = new Date(d);
          if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
        }
        if (d instanceof Date && !isNaN(d.getTime())) return d.toISOString().split('T')[0];
        return null;
      };

      // Normalize helper: convert any time string to HH:mm (handles "7:00 PM" -> "19:00")
      const toTimeString = (t) => {
        if (!t) return null;
        const s = t.toString().trim();
        // Already HH:mm or HH:mm:ss
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(s)) return s.substring(0, 5);
        // 12-hour format with AM/PM
        const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm)$/i);
        if (m) {
          let h = parseInt(m[1], 10);
          const min = m[2];
          const period = m[3].toUpperCase();
          if (period === 'PM' && h !== 12) h += 12;
          if (period === 'AM' && h === 12) h = 0;
          return `${String(h).padStart(2, '0')}:${min}`;
        }
        return s; // return as-is if unrecognised
      };

      const startDateStr = toDateString(taskData.startDate);
      const deadlineDateStr = toDateString(taskData.deadline);
      const followUpDateStr = toDateString(taskData.nextFollowUpDate);
      const startTimeStr = toTimeString(taskData.startTime);
      const endTimeStr = toTimeString(taskData.endTime);
      const followUpTimeStr = toTimeString(taskData.nextFollowUpTime);

      // Format dates with validation (including nextFollowUpDate)
      // Create dates in local timezone to preserve the exact time user selected
      const startDateTime = startDateStr && startTimeStr
        ? new Date(`${startDateStr}T${startTimeStr}:00`)
        : null;
      const endDateTime = deadlineDateStr && endTimeStr
        ? new Date(`${deadlineDateStr}T${endTimeStr}:00`)
        : null;
      const nextFollowUpDateTime = followUpDateStr && followUpTimeStr
        ? new Date(`${followUpDateStr}T${followUpTimeStr}:00`)
        : null;

      if (startDateTime && isNaN(startDateTime.getTime())) {
        throw new Error("Invalid start date");
      }
      if (endDateTime && isNaN(endDateTime.getTime())) {
        throw new Error("Invalid end date");
      }
      if (nextFollowUpDateTime && isNaN(nextFollowUpDateTime.getTime())) {
        throw new Error("Invalid next follow-up date");
      }

      const companyId = await getCompanyId();

      // Defensive checks
      if (!department) {
        throw new Error("Department is required");
      }
      if (!selectedEmployees || selectedEmployees.length === 0) {
        throw new Error("At least one assignee is required");
      }

      // NEW: Separate new files from existing attachments (for edits)
      const newFiles = attachments.filter(att => att.file instanceof File);  // Only local files with File object

      // Create FormData for multipart upload
      const formData = new FormData();

      // Append text fields
      formData.append('title', taskData.title?.trim() || '');
      formData.append('description', taskData.description?.trim() || '');
      formData.append('assignedTo', JSON.stringify(selectedEmployees.map(emp => emp._id || emp.id).filter(Boolean)));  // Array as JSON string (parse on backend)
      formData.append('department', department?.trim() || '');
      formData.append('priority', (taskData.priority || 'Medium').toLowerCase());
      formData.append('status', (taskData.status || 'pending').toLowerCase());
      //  formData.append('creditPoints', taskData.creditPoints || 0);

      // Date fields - Send as ISO strings to preserve timezone
      if (startDateTime) formData.append('startDateTime', startDateTime.toISOString());
      if (endDateTime) formData.append('endDateTime', endDateTime.toISOString());
      if (nextFollowUpDateTime) formData.append('nextFollowUpDateTime', nextFollowUpDateTime.toISOString());
      if (companyId) formData.append('company', companyId);

      // Recurring fields (cleaned up - no duplication)
      if (taskData.repeat) {
        // Validate recurring start date/time
        if (!taskData.recurringStartDate || !taskData.startTime) {
          throw new Error("Recurring start date and time are required.");
        }
        const recurringStart = new Date(`${taskData.recurringStartDate}T${taskData.startTime}:00`);
        if (isNaN(recurringStart.getTime())) {
          throw new Error("Invalid recurring start date.");
        }
        // Validate recurring end date/time
        if (!taskData.recurringEndDate || !taskData.endTime) {
          throw new Error("Recurring end date and time are required.");
        }
        const recurringEnd = new Date(`${taskData.recurringEndDate}T${taskData.endTime}:00`);
        if (isNaN(recurringEnd.getTime())) {
          throw new Error("Invalid recurring end date.");
        }
        formData.append('repeat', 'true');
        formData.append('repeatFrequency', taskData.repeatFrequency || 'daily');
        formData.append('recurringStartDate', recurringStart.toISOString());
        formData.append('recurringEndDate', recurringEnd.toISOString());

        // Add repeatDaysOfWeek for weekly tasks
        if (taskData.repeatFrequency === 'weekly') {
          if (repeatWeekdays.length === 0) {
            throw new Error("Please select at least one weekday for weekly recurring tasks.");
          }
          // Capitalize weekday names for backend (expects 'Monday' not 'monday')
          const capitalizedWeekdays = repeatWeekdays.map(day =>
            day.charAt(0).toUpperCase() + day.slice(1)
          );
          formData.append('repeatDaysOfWeek', JSON.stringify(capitalizedWeekdays));
        }

        // Add repeatDatesOfMonth for monthly tasks
        if (taskData.repeatFrequency === 'monthly') {
          if (repeatMonthDays.length === 0) {
            throw new Error("Please select at least one day of the month for monthly recurring tasks.");
          }
          // Convert string days to numbers for backend
          const monthDaysAsNumbers = repeatMonthDays.map(day => parseInt(day, 10));
          formData.append('repeatDatesOfMonth', JSON.stringify(monthDaysAsNumbers));
        }
        // Backward compatibility for repeatEndDate
        // (nextFinishDateTime removed)
      }

      // UPDATED: Append existing attachments (remaining ones)
      const existingAttachments = attachments.filter(att => !att.file && att.url);  // Server paths
      if (initialData && existingAttachments.length > 0) {
        formData.append('existingImages', JSON.stringify(existingAttachments.filter(a => a.type === 'image').map(a => a.url)));
        formData.append('existingAudios', JSON.stringify(existingAttachments.filter(a => a.type === 'audio').map(a => a.url)));
        formData.append('existingFiles', JSON.stringify(existingAttachments.filter(a => a.type === 'file').map(a => a.url)));
      } else {
        // Send empty arrays if no existing
        formData.append('existingImages', JSON.stringify([]));
        formData.append('existingAudios', JSON.stringify([]));
        formData.append('existingFiles', JSON.stringify([]));
      }


      if (initialData) {
        formData.append('removedImages', JSON.stringify(removedImages));  // e.g., ["https://cloudinary/deleted.png"]
        formData.append('removedAudios', JSON.stringify(removedAudios));
        formData.append('removedFiles', JSON.stringify(removedFiles));
      }

      // NEW: Append NEW files to respective fields (multipart arrays)
      newFiles.forEach((att, index) => {
        if (att.file) {
          if (att.type === 'image') {
            formData.append('images', att.file, att.name);  // 'images' field (array)
          } else if (att.type === 'audio') {
            formData.append('audios', att.file, att.name);  // 'audios' field
          } else if (att.type === 'file') {
            formData.append('files', att.file, att.name);  // 'files' field
          }
        }
      });

      for (let [key, value] of formData.entries()) {
        if (key !== 'images' && key !== 'audios' && key !== 'files') {  // Don't log file contents
        }
      }

      // UPDATED: Validation for multiple employees
      const assignedToArray = selectedEmployees.map(emp => emp._id || emp.id).filter(Boolean);
      if (!assignedToArray || assignedToArray.length === 0) {
        throw new Error("At least one assignee is required");
      }
      if (!department) {
        throw new Error("department is null or undefined");
      }

      // API call
      const isUpdate = Boolean(initialData?.id);
      const endpoint = isUpdate ? `/tasks/${initialData.id}` : '/tasks';  // FIXED: Correct endpoint (assuming route is /api/task/tasks)
      const method = isUpdate ? 'PUT' : 'POST';
      const url = `${API_BASE_URL}/task${endpoint}`;

      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
        // NO 'Content-Type' - let fetch set multipart/form-data with boundary
      };

      const response = await fetch(url, {
        method,
        headers,
        body: formData,  // NEW: FormData instead of JSON
        credentials: 'include'
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        let errorMessage = `Failed to ${isUpdate ? 'update' : 'create'} task (${response.status})`;
        if (responseData.message) {
          errorMessage = responseData.message;
          if (responseData.error) {
            errorMessage += `: ${responseData.error}`;
          }
        } else if (responseData.error) {
          errorMessage = responseData.error;
        }
        throw new Error(errorMessage);
      }


      // On success: Clear removed states
      if (response.ok) {
        setRemovedImages([]);
        setRemovedAudios([]);
        setRemovedFiles([]);
      }

      // UPDATED: Success message for multiple employees
      if (window.toast) {
        window.toast.success(
          `Task ${isUpdate ? 'updated' : 'created'} successfully! Assigned to ${selectedEmployees.length} employee${selectedEmployees.length > 1 ? 's' : ''}.`
        );
      }

      // NEW: Clean up local URLs (revoke to free memory)
      attachments.forEach(att => {
        const url = typeof att?.url === 'string' ? att.url : null;
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      setAttachments([]);  // Clear after successful upload

      const result = responseData.task || responseData.data || responseData;  // Adjust based on backend response structure

      if (typeof onSubmit === 'function') {
        onSubmit(result);
      }

      if (typeof onClose === 'function') {
        onClose();
      }

      return result;
    } catch (error) {
      console.error('❌ Task submission failed:', error);
      setApiError(error.message);

      if (window.toast) {
        window.toast.error(error.message || 'Failed to save task. Please try again.');
      }

      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const loadTaskData = async () => {
      if (!initialData?.id) return;
      console.log("initialData---", initialData);

      try {
        setLoading(true);

        // If we have initialData, set the form fields
        if (initialData) {
          setTitle(initialData.title || '');
          setDescription(initialData.description || '');
          // setTaskPoints(initialData.creditPoints || initialData.points || 0);

          // Set department - handle both ID and object formats
          if (initialData.department) {
            if (typeof initialData.department === 'object') {
              setDepartment(initialData.department.id || initialData.department._id);
            } else {
              setDepartment(initialData.department);
            }
          }

          setPriority(initialData.priority || 'Medium');

          // Parse dates if they exist
          if (initialData.startDate) {
            setStartDate(initialData.startDate);
            setStartTime(initialData.startTime || '09:00');
          }
          if (initialData.deadline) {
            setDeadline(initialData.deadline);
            setEndTime(initialData.endTime || '18:00');
          }

          // Set repeat settings - UPDATED for arrays
          setRepeat(!!initialData.repeat);
          setRepeatFrequency(initialData.repeatFrequency || 'daily');
          setRepeatWeekdays(
            initialData.repeatWeekdays && Array.isArray(initialData.repeatWeekdays)
              ? initialData.repeatWeekdays
              : initialData.repeatWeekday
                ? [initialData.repeatWeekday]
                : []
          );
          setRepeatMonthDays(
            initialData.repeatMonthDays && Array.isArray(initialData.repeatMonthDays)
              ? initialData.repeatMonthDays
              : initialData.repeatMonthDay
                ? [initialData.repeatMonthDay]
                : []
          );
          setRepeatEndDate(initialData.repeatEndDate || '');

          // NEW: Set next follow up date and time
          setNextFollowUpDate(initialData.nextFollowUpDate || '');
          setNextFollowUpTime(initialData.nextFollowUpTime || '09:00');

          // UPDATED: Load attachments by merging images/audios/files from initialData
          const loadedAttachments = [];

          console.log('„ loadTaskData - Processing attachments:', {
            images: initialData.images,
            audios: initialData.audios,
            files: initialData.files
          });

          // Helper function to extract attachment metadata
          const extractAttachment = (item, fallbackType, index) => {
            if (typeof item === 'string') {
              const url = item;
              const name = url.split('/').pop() || `${fallbackType} ${index + 1}`;
              return {
                id: `${fallbackType}-${Date.now()}-${index}`,
                name: name,
                type: fallbackType,
                size: 0,
                url: url,
                uploadDate: initialData.createdAt || new Date().toISOString()
              };
            } else if (typeof item === 'object' && item !== null) {
              const url = item.url || item.path || item.secure_url;
              if (!url) {
                console.warn('⚠️ No URL found in attachment:', item);
                return null;
              }

              // Determine type - prefer explicit type field, fall back to extension
              let type = fallbackType;
              let fileExtension = '';

              if (item.type) {
                // Handle MIME types (e.g., "image/jpeg" -> "image")
                if (item.type.startsWith('image/')) {
                  type = 'image';
                  // Extract extension from MIME type
                  const mimeExt = item.type.split('/')[1];
                  if (mimeExt === 'jpeg') fileExtension = '.jpg';
                  else if (mimeExt) fileExtension = `.${mimeExt}`;
                } else if (item.type.startsWith('audio/')) {
                  type = 'audio';
                  const mimeExt = item.type.split('/')[1];
                  if (mimeExt) fileExtension = `.${mimeExt}`;
                } else if (item.type.startsWith('application/') || item.type.startsWith('text/')) {
                  type = 'file';
                  const mimeExt = item.type.split('/')[1];
                  // Common MIME type mappings
                  if (mimeExt === 'pdf') fileExtension = '.pdf';
                  else if (mimeExt === 'msword') fileExtension = '.doc';
                  else if (mimeExt === 'vnd.openxmlformats-officedocument.wordprocessingml.document') fileExtension = '.docx';
                  else if (mimeExt === 'vnd.ms-excel') fileExtension = '.xls';
                  else if (mimeExt === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') fileExtension = '.xlsx';
                  else if (mimeExt === 'plain') fileExtension = '.txt';
                  else if (mimeExt) fileExtension = `.${mimeExt.split('.').pop()}`;
                } else if (item.type === 'image' || item.type === 'audio' || item.type === 'file') {
                  type = item.type;
                }
              } else {
                // Fallback to extension-based detection
                const ext = url.split('.').pop()?.toLowerCase();
                if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
                  type = 'image';
                  fileExtension = `.${ext}`;
                } else if (['mp3', 'wav', 'm4a', 'ogg'].includes(ext)) {
                  type = 'audio';
                  fileExtension = `.${ext}`;
                } else {
                  type = 'file';
                  if (ext) fileExtension = `.${ext}`;
                }
              }

              // Get the base name and ensure it has proper extension
              let fileName = item.name || item.filename || item.originalname;

              // If no filename, try to extract from URL
              if (!fileName || fileName.trim() === '') {
                // Extract filename from URL, removing Cloudinary transforms
                const urlParts = url.split('/');
                fileName = urlParts[urlParts.length - 1].split('?')[0] || `${fallbackType}_${index + 1}`;
              }

              // Ensure filename has proper extension
              const hasExtension = fileName.includes('.') && fileName.lastIndexOf('.') > 0;

              if (!hasExtension && fileExtension) {
                // No extension found, add the determined one
                fileName = fileName + fileExtension;
              } else if (hasExtension) {
                // Has extension, but verify it's correct
                const currentExt = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
                // If current extension is too long (like Cloudinary ID), replace it
                if (currentExt.length > 6 && fileExtension) {
                  fileName = fileName.substring(0, fileName.lastIndexOf('.')) + fileExtension;
                }
              } else if (!fileExtension) {
                // No extension determined, try to get from URL
                const urlExt = url.split('.').pop()?.split('?')[0]?.toLowerCase();
                if (urlExt && urlExt.length <= 5 && !['com', 'net', 'org'].includes(urlExt)) {
                  fileName = fileName + '.' + urlExt;
                }
              }

              console.log('Ž Extracted attachment:', {
                originalName: item.name,
                finalFileName: fileName,
                type,
                fileExtension,
                url
              });

              return {
                id: item._id || item.id || `${type}-${Date.now()}-${index}`,
                name: fileName,
                type: type,
                size: item.size || 0,
                url: url,
                uploadDate: item.uploadDate || item.createdAt || initialData.createdAt || new Date().toISOString()
              };
            }
            return null;
          };

          // Handle images
          if (initialData.images && Array.isArray(initialData.images) && initialData.images.length > 0) {
            initialData.images.forEach((img, index) => {
              const attachment = extractAttachment(img, 'image', index);
              if (attachment) {
                loadedAttachments.push(attachment);
                console.log('✅ Loaded image:', attachment);
              }
            });
          }

          // Handle audios
          if (initialData.audios && Array.isArray(initialData.audios) && initialData.audios.length > 0) {
            initialData.audios.forEach((aud, index) => {
              const attachment = extractAttachment(aud, 'audio', index);
              if (attachment) {
                loadedAttachments.push(attachment);
                console.log('✅ Loaded audio:', attachment);
              }
            });
          }

          // Handle files
          if (initialData.files && Array.isArray(initialData.files) && initialData.files.length > 0) {
            initialData.files.forEach((fileItem, index) => {
              const attachment = extractAttachment(fileItem, 'file', index);
              if (attachment) {
                loadedAttachments.push(attachment);
                console.log('✅ Loaded file:', attachment);
              }
            });
          }

          // Also check for legacy unified attachments (if backend ever uses it)
          if (initialData.attachments && Array.isArray(initialData.attachments) && initialData.attachments.length > 0) {
            initialData.attachments.forEach((att, index) => {
              const attachment = extractAttachment(att, 'file', index);
              if (attachment) loadedAttachments.push(attachment);
            });
          }

          console.log('Ž Total attachments loaded:', loadedAttachments.length, loadedAttachments);
          setAttachments(loadedAttachments);
          // NEW: Reset removals for this edit session
          setRemovedImages([]);
          setRemovedAudios([]);
          setRemovedFiles([]);

          console.log('Ž Loaded existing attachments for edit:', loadedAttachments.length, loadedAttachments);  // Debug: Check console
        }
      } catch (error) {
        console.error('Failed to load task data:', error);
        setApiError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadTaskData();
  }, [initialData]);


  // Load data on component mount - UPDATED for multiple assignees
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setApiError(null);

        await fetchCompanyDepartments();
        console.log("initialData", initialData);

        // If editing and we have department data, fetch employees for that department
        if (initialData?.department) {
          let deptId = '';
          if (typeof initialData.department === 'object') {
            deptId = initialData.department._id || initialData.department.id || '';
          } else {
            deptId = initialData.department;
          }

          if (deptId) {
            setDepartment(deptId);
            const employeesData = await fetchDepartmentEmployees(deptId);

            // FIXED: After employees are loaded, set the assigned employees immediately
            if (initialData.assignees?.length > 0 && employeesData?.length > 0) {
              const assigneeNames = initialData.assignees.map(assignee => {
                if (typeof assignee === 'object') {
                  return assignee.teamMemberName || assignee.name ||
                    `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim();
                }
                return assignee;
              });

              // Find matching employees from the fetched data
              const selectedEmps = employeesData.filter(emp => {
                const empName = emp.teamMemberName?.trim() ||
                  emp.name?.trim() ||
                  `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                return assigneeNames.some(assigneeName =>
                  empName.toLowerCase() === assigneeName.toLowerCase()
                );
              });

              if (selectedEmps.length > 0) {
                setSelectedEmployees(selectedEmps);
                setAssignees(assigneeNames);
              }
            }
          }
        }

      } catch (error) {
        console.error('Component loading failed:', error);
        setApiError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [initialData]);

  // Update employees when department changes - UPDATED for multiple assignees
  useEffect(() => {
    if (department && departmentObjects.length > 0) {
      fetchDepartmentEmployees(department);
    } else {
      setEmployees([]);
    }
  }, [department, departmentObjects]);

  // Enhanced employee name processing - UPDATED
  const allEmployeeNames = React.useMemo(() => {
    if (!employees || employees.length === 0) {
      return [];
    }


    return employees.map(employee => {
      const name = employee.teamMemberName?.trim() ||
        employee.name?.trim() ||
        `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
        `Employee ${employee._id?.substring(0, 8) || 'Unknown'}`;

      return name;
    }).filter(name => name && name !== 'Employee undefined' && name !== 'Employee Unknown');
  }, [employees]);

  // Filter employees by search term
  const filteredEmployees = React.useMemo(() => {
    if (!searchTerm) return allEmployeeNames;

    return allEmployeeNames.filter(name =>
      name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allEmployeeNames, searchTerm]);

  // UPDATED: Toggle functions for multiple selections
  const toggleWeekday = (day) => {
    setRepeatWeekdays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  const toggleMonthDay = (day) => {
    setRepeatMonthDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  // UPDATED: Toggle user selection - Allow multiple employees
  const toggleAssignee = (userName) => {
    const isSelected = selectedEmployees.some(emp => {
      const empName = emp.teamMemberName?.trim() ||
        emp.name?.trim() ||
        `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
      return empName === userName;
    });

    if (isSelected) {
      // Remove employee
      setSelectedEmployees(selectedEmployees.filter(emp => {
        const empName = emp.teamMemberName?.trim() ||
          emp.name?.trim() ||
          `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
        return empName !== userName;
      }));
      setAssignees(assignees.filter(name => name !== userName));
    } else {
      // Add employee - requires finding them in currently active employees list
      const employee = employees.find(emp => {
        const empName = emp.teamMemberName?.trim() ||
          emp.name?.trim() ||
          `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
        return empName === userName;
      });

      if (!employee) return;

      // CHANGED: Add employee to existing selection instead of replacing
      setSelectedEmployees([...selectedEmployees, employee]);
      setAssignees([...assignees, userName]);
    }
  };

  const validateForm = async () => {
    const errors = {};

    if (!title.trim()) errors.title = 'Title is required';
    if (!description.trim()) errors.description = 'Description is required';

    // NEW: Conditional validation based on recurring status
    if (repeat) {
      // Validate recurring dates
      if (!recurringStartDate) errors.recurringStartDate = 'Recurring start date is required';
      if (!recurringEndDate) errors.recurringEndDate = 'Recurring end date is required';

      // Holiday validation removed - dates will be auto-shifted on submit if needed
    } else {
      // Validate regular schedule dates
      if (!startDate) errors.startDate = 'Start date is required';
      if (!deadline) errors.deadline = 'End date is required';

      // Holiday validation removed - dates will be auto-shifted on submit
    }

    if (selectedEmployees.length === 0) errors.assignees = 'At least one employee must be assigned';

    // Employee shift and schedule validation (only for non-recurring tasks or use recurring dates)
    if (selectedEmployees.length > 0 && startDate && startTime && endTime && !repeat) {
      try {
        const scheduleViolations = await validateEmployeeSchedule(
          selectedEmployees,
          startDate,
          startTime,
          endTime
        );

        if (scheduleViolations.length > 0) {
          errors.schedule = scheduleViolations.join('; ');
        }
      } catch (error) {
        console.error('Schedule validation error:', error);
        errors.schedule = 'Unable to validate employee schedules - please try again';
      }
    }

    setFormErrors(errors);

    // Show validation errors in snackbar
    if (Object.keys(errors).length > 0) {
      const errorMessage = Object.values(errors)[0]; // Show first error
      setSnackbar({
        open: true,
        message: errorMessage,
        type: 'error'
      });
    }

    return Object.keys(errors).length === 0;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const isValid = await validateForm();
      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      // Check and shift dates if they fall on holidays
      let adjustedStartDate = repeat ? recurringStartDate : startDate;
      let adjustedDeadline = repeat ? recurringEndDate : deadline;
      let adjustedFollowUpDate = nextFollowUpDate;
      let dateAdjustments = [];

      console.log('Holiday Check - Original Dates:', {
        isRepeat: repeat,
        regularTask: { startDate, deadline },
        recurringTask: { recurringStartDate, recurringEndDate },
        dateToCheckStart: repeat ? recurringStartDate : startDate,
        dateToCheckEnd: repeat ? recurringEndDate : deadline,
        nextFollowUpDate,
        isStartHoliday: repeat ? (recurringStartDate ? isHoliday(recurringStartDate) : false) : isHoliday(startDate),
        isDeadlineHoliday: repeat ? (recurringEndDate ? isHoliday(recurringEndDate) : false) : isHoliday(deadline),
        isFollowUpHoliday: nextFollowUpDate ? isHoliday(nextFollowUpDate) : false,
        holidaysCount: holidays.length,
        holidays: holidays
      });

      // Check and adjust dates for both regular and recurring tasks
      const dateToCheckStart = repeat ? recurringStartDate : startDate;
      const dateToCheckEnd = repeat ? recurringEndDate : deadline;

      // Check start date (for both regular and recurring tasks)
      if (dateToCheckStart && isHoliday(dateToCheckStart)) {
        const workingStartDate = getPreviousWorkingDate(dateToCheckStart);
        console.log('„ Shifting start date:', { from: dateToCheckStart, to: workingStartDate, isRecurring: repeat });

        if (workingStartDate !== dateToCheckStart) {
          adjustedStartDate = workingStartDate;
          dateAdjustments.push(
            `${repeat ? 'Recurring start' : 'Start'} date shifted from ${new Date(dateToCheckStart).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })} to ${new Date(workingStartDate).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} (holiday avoided)`
          );
        }
      }

      // Check end date (for both regular and recurring tasks)
      if (dateToCheckEnd && isHoliday(dateToCheckEnd)) {
        const workingDeadline = getPreviousWorkingDate(dateToCheckEnd);
        console.log('„ Shifting end date:', { from: dateToCheckEnd, to: workingDeadline, isRecurring: repeat });

        if (workingDeadline !== dateToCheckEnd) {
          adjustedDeadline = workingDeadline;
          dateAdjustments.push(
            `${repeat ? 'Recurring end' : 'End'} date shifted from ${new Date(dateToCheckEnd).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })} to ${new Date(workingDeadline).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} (holiday avoided)`
          );
        }
      }

      // Check next follow-up date (only for non-recurring tasks)
      if (!repeat && nextFollowUpDate && isHoliday(nextFollowUpDate)) {
        const workingFollowUpDate = getPreviousWorkingDate(nextFollowUpDate);
        console.log('„ Shifting next follow-up date:', { from: nextFollowUpDate, to: workingFollowUpDate });

        if (workingFollowUpDate !== nextFollowUpDate) {
          adjustedFollowUpDate = workingFollowUpDate;
          dateAdjustments.push(
            `Follow-up date shifted from ${new Date(nextFollowUpDate).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })} to ${new Date(workingFollowUpDate).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} (holiday avoided)`
          );
        }
      }

      // Log the dates being used for task creation
      console.log('… Creating task with dates:', {
        originalStartDate: startDate,
        adjustedStartDate,
        originalDeadline: deadline,
        adjustedDeadline,
        originalFollowUpDate: nextFollowUpDate,
        adjustedFollowUpDate,
        wasAdjusted: dateAdjustments.length > 0,
        adjustments: dateAdjustments
      });

      const taskData = {
        id: initialData?.id,
        title,
        description,
        assignees: selectedEmployees,
        department,
        priority,
        startDate: adjustedStartDate,
        startTime,
        deadline: adjustedDeadline,
        endTime,
        nextFollowUpDate: adjustedFollowUpDate,
        nextFollowUpTime,
        repeat,
        repeatFrequency,
        repeatWeekdays,
        repeatMonthDays,
        recurringStartDate: repeat ? adjustedStartDate : recurringStartDate,  // Use adjusted date for recurring tasks
        recurringEndDate: repeat ? adjustedDeadline : recurringEndDate,        // Use adjusted date for recurring tasks
        repeatEndDate: repeat ? adjustedDeadline : recurringEndDate,           // Keep for backward compatibility
        attachments,
        status: initialData?.status || 'pending',
        createdAt: initialData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),

      };

      await submitTask(taskData);

      // Show success message with date adjustments if any
      let successMessage = `Task ${initialData?.id ? 'updated' : 'created'} successfully! Assigned to ${selectedEmployees.length} employee${selectedEmployees.length > 1 ? 's' : ''}.`;

      if (dateAdjustments.length > 0) {
        successMessage += ' … ' + dateAdjustments.join(' • ');
      }

      // Close form immediately and let parent handle success message
      onClose();

    } catch (error) {
      console.error('Form submission error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save task. Please check employee schedules and try again.',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  // UPDATED: handleFileUpload - Handle images, audio, and generic files (PDF, DOCX, etc.)
  const handleFileUpload = (e, type) => {
    const files = Array.from(e.target.files).filter(
      (file) => file && file.size > 0 // Skip 0-byte files
    );

    if (files.length === 0) {
      console.warn('⚠️ No valid files selected');
      e.target.value = '';
      return;
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const oversizedFiles = files.filter(file => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      setSnackbar({
        open: true,
        message: `${oversizedFiles.length} file(s) exceed the 5MB limit and were not added.`,
        type: 'error'
      });
      e.target.value = '';
      return;
    }

    const newAttachments = files.map((file) => {
      const attachment = {
        id: Date.now() + Math.random(),
        name: file.name,
        type: type,
        size: file.size,
        url: URL.createObjectURL(file),
        file: file,  // For upload
        uploadDate: new Date().toISOString()
      };
      console.log('Ž New attachment added:', attachment);
      return attachment;
    });

    setAttachments(prev => {
      const updated = [...prev, ...newAttachments];
      console.log('📋 All attachments after upload:', updated);
      return updated;
    });

    setSnackbar({
      open: true,
      message: `${files.length} file(s) added successfully!`,
      type: 'success'
    });

    e.target.value = '';  // Reset
  };


  // UPDATED: Remove attachment – Distinguish existing vs. new, track deletions
  const removeAttachment = (id) => {
    const attachment = attachments.find(att => att.id === id);
    if (!attachment) return;
    // If it's an EXISTING server attachment (has url but no file), track for backend removal
    if (!attachment.file && attachment.url) {
      if (attachment.type === 'image') {
        setRemovedImages(prev => [...prev, attachment.url]);  // Add URL to removed list
      } else if (attachment.type === 'audio') {
        setRemovedAudios(prev => [...prev, attachment.url]);
      } else if (attachment.type === 'file') {
        setRemovedFiles(prev => [...prev, attachment.url]);
      }
    }
    // Always remove from local UI state
    setAttachments(prev => prev.filter(att => att.id !== id));

    console.log('—ï Removed attachment:', { id, type: attachment.type, url: attachment.url });  // Debug
  };

  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const audioChunks = [];
      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunks, { type: 'audio/wav' });
        const file = new File([blob], `recording-${Date.now()}.wav`, { type: 'audio/wav' });  // NEW: Create File object
        const newAttachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          type: 'audio',
          size: file.size,
          url: URL.createObjectURL(blob),  // Preview
          file: file,  // NEW: Preserve File for upload
          uploadDate: new Date().toISOString()
        };
        setAttachments(prev => [...prev, newAttachment]);  // Append
        stream.getTracks().forEach(track => track.stop());  // Stop stream
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setShowAlert('Could not access microphone. Please check permissions.');
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  // Play audio attachment
  const playAudio = (url) => {
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play();
      setIsPlaying(true);

      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
          setDuration(audioRef.current.duration || 0);

          if (audioRef.current.ended) {
            setIsPlaying(false);
            setCurrentTime(0);
            clearInterval(progressIntervalRef.current);
          }
        }
      }, 100);
    }
  };

  // Pause audio
  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      clearInterval(progressIntervalRef.current);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format time for audio player
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Get file icon based on type
  const getFileIcon = (type) => {
    switch (type) {
      case 'image':
        return (
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
          </div>
        );
      case 'audio':
        return (
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path>
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
        );
    }
  };

  // Handle click outside form
  const handleClickOutside = (e) => {
    if (formRef.current && !formRef.current.contains(e.target)) {
      if (hasUnsavedChanges()) {
        setConfirmDialog({
          open: true,
          title: 'Discard Changes?',
          message: 'You have unsaved changes. Are you sure you want to close this form? All changes will be lost.',
          type: 'warning',
          confirmText: 'Discard',
          cancelText: 'Continue Editing',
          onConfirm: () => {
            setConfirmDialog({ ...confirmDialog, open: false });
            onClose();
          }
        });
      }
    }
  };

  // Helper function to check if form has unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialData) {
      // New task - check if any field has been filled
      return title || description || selectedEmployees.length > 0 || attachments.length > 0;
    }
    // Editing task - check if anything changed
    return (
      title !== (initialData.title || '') ||
      description !== (initialData.description || '') ||
      JSON.stringify(selectedEmployees.sort()) !== JSON.stringify((initialData.assignees || []).sort()) ||
      department !== (initialData.department || '') ||
      priority !== (initialData.priority || 'Medium')
    );
  };

  // Handle close with confirmation if there are unsaved changes
  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setConfirmDialog({
        open: true,
        title: 'Discard Changes?',
        message: 'You have unsaved changes. Are you sure you want to close this form? All changes will be lost.',
        type: 'warning',
        confirmText: 'Discard',
        cancelText: 'Continue Editing',
        onConfirm: () => {
          setConfirmDialog({ ...confirmDialog, open: false });
          onClose();
        }
      });
    } else {
      onClose();
    }
  };

  // Clean up on unmount
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Set next follow up date to start date by default
  useEffect(() => {
    if (!initialData && startDate && !nextFollowUpDate) {
      setNextFollowUpDate(startDate);
    }
  }, [startDate, initialData, nextFollowUpDate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (showDropdown && !event.target.closest('.employee-dropdown')) {
        setShowDropdown(false);
      }
      if (showDeptDropdown && !event.target.closest('.dept-dropdown')) {
        setShowDeptDropdown(false);
      }
      if (showPriorityDropdown && !event.target.closest('.priority-dropdown')) {
        setShowPriorityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideDropdown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDropdown);
    };
  }, [showDropdown, showDeptDropdown, showPriorityDropdown]);

  // Get priority color
  const getPriorityColor = (priorityValue) => {
    const priorityObj = priorities.find(p => p.value === priorityValue);
    return priorityObj ? priorityObj.color : '#6E6E6E';
  };

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4 z-[9999] overflow-hidden">
      <div className="bg-[#fff] rounded-none sm:rounded-2xl shadow-xl w-full max-w-4xl h-full sm:h-auto max-h-full sm:max-h-[90vh] flex flex-col overflow-hidden animate-slideUp" ref={formRef}>

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-3 sm:p-6 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={initialData ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" : "M12 6v6m0 0v6m0-6h6m-6 0H6"}></path>
                </svg>
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-white">
                  {initialData ? 'Edit Task' : 'Create New Task'}
                </h2>
                <p className="text-xs sm:text-sm text-indigo-100">
                  {employees.length} employees in {
                    (() => {
                      if (!department) return 'selected department';
                      const dept = departmentObjects.find(d => d._id === department || d.id === department) ||
                        departments.find(d => d.id === department);
                      return dept ? `${dept.name || dept.departmentName} ` : 'selected department';
                    })()
                  } • {selectedEmployees.length} assigned
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-6 overflow-y-auto flex-1 pb-32 sm:pb-6 relative min-h-[300px]">
          {loading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-white z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Loading Data</h3>
              <p className="text-gray-600 text-center">Fetching departments, employees, and holidays...</p>
            </div>
          ) : apiError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-white z-10">
              <div className="text-center max-w-md w-full">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Data</h3>
                <p className="text-gray-600 mb-6">{apiError}</p>
                <div className="flex gap-3 max-w-xs mx-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setApiError(null);
                      setLoading(true);
                      fetchCompanyDepartments().finally(() => setLoading(false));
                    }}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors text-sm"
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 text-gray-700 hover:text-gray-900 font-medium rounded-lg border border-gray-300 hover:border-gray-400 text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6">

            {/* NEW: Holiday Loading/Error State */}
            {(loadingHolidays || holidayError) && (
              <div className={`rounded-xl p-4 border ${holidayError ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${holidayError ? 'bg-red-100' : 'bg-blue-100'}`}>
                    {loadingHolidays ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ) : (
                      <svg className={`w-4 h-4 ${holidayError ? 'text-red-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={holidayError ? "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" : "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"}></path>
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${holidayError ? 'text-red-800' : 'text-blue-800'}`}>
                      {loadingHolidays ? 'Loading company holidays...' : 'Holiday data unavailable'}
                    </p>
                    {holidayError && (
                      <p className="text-xs text-red-600 mt-1">
                        {holidayError}
                        <button
                          type="button"
                          onClick={fetchHolidays}
                          className="ml-2 underline hover:no-underline"
                        >
                          Retry
                        </button>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* NEW: Date Shift Alert Notification */}
            {showAlert && (
              <div className={`rounded-xl p-4 border ${showAlert.type === 'error' ? 'bg-red-50 border-red-200' :
                showAlert.type === 'success' ? 'bg-green-50 border-green-200' :
                  'bg-yellow-50 border-yellow-200'
                }`}>
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${showAlert.type === 'error' ? 'bg-red-100' :
                    showAlert.type === 'success' ? 'bg-green-100' :
                      'bg-yellow-100'
                    }`}>
                    <svg className={`w-5 h-5 ${showAlert.type === 'error' ? 'text-red-600' :
                      showAlert.type === 'success' ? 'text-green-600' :
                        'text-yellow-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={
                        showAlert.type === 'success'
                          ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          : "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      }></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className={`text-sm font-medium mb-1 ${showAlert.type === 'error' ? 'text-red-800' :
                      showAlert.type === 'success' ? 'text-green-800' :
                        'text-yellow-800'
                      }`}>
                      {showAlert.type === 'error' ? 'Date Selection Error' :
                        showAlert.type === 'success' ? 'Task Created - Dates Adjusted' :
                          'Date Automatically Adjusted'}
                    </h4>
                    <p className={`text-sm ${showAlert.type === 'error' ? 'text-red-700' :
                      showAlert.type === 'success' ? 'text-green-700' :
                        'text-yellow-700'
                      }`}>
                      {showAlert.message}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAlert(null)}
                    className={`flex-shrink-0 ${showAlert.type === 'error' ? 'text-red-400 hover:text-red-600' :
                      showAlert.type === 'success' ? 'text-green-400 hover:text-green-600' :
                        'text-yellow-400 hover:text-yellow-600'
                      }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Schedule Conflicts Warning */}
            {formErrors.schedule && (
              <div className="rounded-xl p-4 bg-yellow-50 border border-yellow-200">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 mb-1">Schedule Conflicts Detected</h4>
                    <p className="text-sm text-yellow-700">{formErrors.schedule}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Basic Information Section */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Task Information
              </h3>

              <div className="space-y-3 sm:space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                    Task Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${formErrors.title ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (formErrors.title) setFormErrors({ ...formErrors, title: '' });
                    }}
                    required
                    placeholder="Enter a descriptive task title..."
                  />
                  {formErrors.title && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                      </svg>
                      {formErrors.title}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none ${formErrors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                    rows="3"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (formErrors.description) setFormErrors({ ...formErrors, description: '' });
                    }}
                    required
                    placeholder="Provide detailed task requirements, objectives, and expected outcomes..."
                  />
                  {formErrors.description && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                      </svg>
                      {formErrors.description}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Configuration Section */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                </svg>
                Task Configuration
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {/* Department */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Department</label>
                  <div className="relative dept-dropdown">
                    <div
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 text-sm border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-colors bg-white cursor-pointer flex justify-between items-center min-h-[42px] sm:min-h-[48px]"
                      onClick={() => setShowDeptDropdown(!showDeptDropdown)}
                    >
                      <span className={department ? 'text-gray-900 text-sm' : 'text-gray-500 text-sm'}>
                        {department ?
                          (departments.find(d => d.id === department)?.name || departments.find(d => d.id === department)?.display?.split(' (')[0] || `Department ${department}`)
                          : 'Select Department'}
                      </span>
                      <div className="text-gray-500 pointer-events-none">
                        <svg className={`w-4 h-4 transition-transform duration-200 ${showDeptDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>

                    {/* Custom Dropdown Options */}
                    {showDeptDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        <div
                          className={`px-3 py-2.5 sm:px-4 sm:py-3 cursor-pointer hover:bg-gray-50 transition-colors text-gray-500 italic text-sm`}
                          onClick={() => {
                            setDepartment('');
                            setSearchTerm('');
                            setShowDeptDropdown(false);
                          }}
                        >
                          Clear Selection
                        </div>
                        {departments.map((dept) => (
                          <div
                            key={dept.id}
                            className={`px-3 py-2.5 sm:px-4 sm:py-3 cursor-pointer transition-colors text-sm ${department === dept.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                            onClick={() => {
                              setDepartment(dept.id);
                              setSearchTerm('');
                              setShowDeptDropdown(false);
                            }}
                          >
                            {dept.name || dept.display?.split(' (')[0] || `Department ${dept.id}`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Priority Level</label>
                  <div className="relative priority-dropdown">
                    <div
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 text-sm border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-colors bg-white cursor-pointer flex justify-between items-center min-h-[42px] sm:min-h-[48px]"
                      onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                    >
                      <span className="text-sm font-medium" style={{ color: getPriorityColor(priority) }}>
                        {priorities.find(p => p.value === priority)?.label || priority}
                      </span>
                      <div className="pointer-events-none flex items-center">
                        <div
                          className="w-6 h-6 rounded-md flex items-center justify-center mr-2"
                          style={{ backgroundColor: getPriorityColor(priority) }}
                        >
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                          </svg>
                        </div>
                        <div className="text-gray-500">
                          <svg className={`w-4 h-4 transition-transform duration-200 ${showPriorityDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Custom Dropdown Options */}
                    {showPriorityDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        {priorities.map((p) => (
                          <div
                            key={p.value}
                            className={`px-3 py-2.5 sm:px-4 sm:py-3 cursor-pointer transition-colors flex items-center text-sm ${priority === p.value ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                            onClick={() => {
                              setPriority(p.value);
                              setShowPriorityDropdown(false);
                            }}
                          >
                            <span className="font-medium" style={{ color: p.color }}>{p.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {department && (
              <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                  </svg>
                  Employee Assignment ({employees.length} available)
                </h3>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                    Select Team Members <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-500 ml-2 bg-gray-100 px-2 py-0.5 rounded-full">
                      {selectedEmployees.length} selected
                    </span>
                  </label>
                  <div className="relative employee-dropdown">
                    <div
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-sm border rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-colors cursor-pointer bg-white flex flex-wrap gap-1.5 sm:gap-2 min-h-[42px] sm:min-h-[48px] items-center ${formErrors.assignees ? 'border-red-500' : 'border-gray-300'
                        }`}
                      onClick={() => setShowDropdown(!showDropdown)}
                    >
                      {assignees.length > 0 ? (
                        assignees.map(user => (
                          <span key={user} className="inline-flex items-center px-2 py-0.5 sm:px-3 sm:py-1 bg-indigo-100 text-indigo-800 rounded-md text-xs sm:text-sm font-medium">
                            <div className="w-4 h-4 bg-indigo-200 rounded-full flex items-center justify-center mr-2 text-xs font-bold">
                              {user.charAt(0).toUpperCase()}
                            </div>
                            {user}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAssignee(user);
                              }}
                              className="ml-2 w-4 h-4 bg-indigo-200 rounded-full flex items-center justify-center hover:bg-indigo-300 transition-colors"
                            >
                              <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                              </svg>
                            </button>
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500">
                          Click to select employees from this department
                        </span>
                      )}
                    </div>

                    {formErrors.assignees && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                        </svg>
                        {formErrors.assignees}
                      </div>
                    )}

                    {showDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="p-3 border-b border-gray-200">
                          <div className="relative">
                            <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                            <input
                              type="text"
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                              placeholder="Search employees..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>

                        <div className="max-h-48 overflow-y-auto">
                          {filteredEmployees.length > 0 ? (
                            filteredEmployees.map(user => (
                              <div
                                key={user}
                                className={`px-3 py-2.5 sm:px-4 sm:py-3 cursor-pointer transition-colors flex items-center ${assignees.includes(user) ? 'bg-indigo-50 border-l-4 border-indigo-500' : 'hover:bg-gray-50'
                                  }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAssignee(user);
                                  if (formErrors.assignees) setFormErrors({ ...formErrors, assignees: '' });
                                }}
                              >
                                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center mr-3 transition-colors ${assignees.includes(user)
                                  ? 'bg-indigo-500 border-indigo-500 text-white'
                                  : 'border-gray-300 bg-gray-100'
                                  }`}>
                                  {assignees.includes(user) ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                    </svg>
                                  ) : (
                                    <span className="text-gray-600 font-bold text-xs">{user.charAt(0).toUpperCase()}</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <span className={`font-medium ${assignees.includes(user) ? 'text-indigo-700' : 'text-gray-700'}`}>
                                    {user}
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-6 text-center">
                              <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                              </svg>
                              <p className="text-gray-500 text-sm">
                                {searchTerm ? `No employees match "${searchTerm}"` : 'No employees available in this department'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected Employees Summary */}
                  {selectedEmployees.length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2 flex items-center text-sm">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Task will be assigned to {selectedEmployees.length} team member{selectedEmployees.length !== 1 ? 's' : ''}:
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {assignees.map(name => (
                          <div key={name} className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                            {name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* REORGANIZED: Recurring Task Section - NOW BEFORE Schedule & Timeline */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center justify-between">
                <span className="flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  Recurring Task
                </span>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => setRepeat(!repeat)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${repeat ? 'bg-indigo-600' : 'bg-gray-300'
                      }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${repeat ? 'translate-x-6' : 'translate-x-1'
                      }`}></span>
                  </button>
                  <span className="text-sm text-gray-600">{repeat ? 'Enabled' : 'Disabled'}</span>
                </div>
              </h3>

              {repeat && (
                <div className="bg-[#f8faff] rounded-xl p-3 sm:p-5 border border-indigo-100/80 space-y-4">
                  {/* Recurring Date Range */}
                  <div>
                    <h4 className="font-semibold text-xs sm:text-sm text-indigo-900 mb-2.5 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      Recurrence Period
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Recurring Start Date */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Start Recurrence <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-row gap-2">
                          <div className="relative flex-[3]">
                            <input
                              type="date"
                              className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${formErrors.recurringStartDate ? 'border-red-500' : 'border-gray-300'
                                } ${isHoliday(recurringStartDate) ? 'bg-red-50 border-red-300' : 'bg-white'}`}
                              value={recurringStartDate}
                              min={today}
                              onChange={(e) => {
                                setRecurringStartDate(e.target.value);
                                if (formErrors.recurringStartDate) setFormErrors({ ...formErrors, recurringStartDate: '' });
                              }}
                              required={repeat}
                            />
                          </div>
                          <div className="relative flex-[2]">
                            <input
                              type="time"
                              className="w-full px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                            />
                          </div>
                        </div>
                        {isHoliday(recurringStartDate) && (
                          <div className="mt-1 text-xs text-red-600 flex items-center">
                            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                            </svg>
                            This date is a holiday. It will shift to the previous working day.
                          </div>
                        )}
                        {formErrors.recurringStartDate && (
                          <div className="mt-1 text-xs text-red-600 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                            </svg>
                            {formErrors.recurringStartDate}
                          </div>
                        )}
                      </div>

                      {/* Recurring End Date */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          End Recurrence <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-row gap-2">
                          <div className="relative flex-[3]">
                            <input
                              type="date"
                              className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${formErrors.recurringEndDate ? 'border-red-500' : 'border-gray-300'
                                } ${isHoliday(recurringEndDate) ? 'bg-red-50 border-red-300' : 'bg-white'}`}
                              value={recurringEndDate}
                              onChange={(e) => {
                                setRecurringEndDate(e.target.value);
                                setRepeatEndDate(e.target.value);
                                if (formErrors.recurringEndDate) setFormErrors({ ...formErrors, recurringEndDate: '' });
                              }}
                              required={repeat}
                            />
                          </div>
                          <div className="relative flex-[2]">
                            <input
                              type="time"
                              className="w-full px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                            />
                          </div>
                        </div>
                        {isHoliday(recurringEndDate) && (
                          <div className="mt-1 text-xs text-red-600 flex items-center">
                            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                            </svg>
                            This date is a holiday. It will shift to the previous working day.
                          </div>
                        )}
                        {formErrors.recurringEndDate && (
                          <div className="mt-1 text-xs text-red-600 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                            </svg>
                            {formErrors.recurringEndDate}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date Range Summary */}
                    {recurringStartDate && recurringEndDate && (
                      <div className="mt-2.5 text-xs text-indigo-700 bg-indigo-50/60 px-3 py-2 rounded-lg flex items-center justify-between border border-indigo-100/50">
                        <span>Tasks repeat from <strong>{new Date(recurringStartDate).toLocaleDateString('en-GB')}</strong> to <strong>{new Date(recurringEndDate).toLocaleDateString('en-GB')}</strong></span>
                        <span className="font-semibold">{Math.ceil((new Date(recurringEndDate) - new Date(recurringStartDate)) / (1000 * 60 * 60 * 24))} days</span>
                      </div>
                    )}
                  </div>

                  {/* Frequency Selection */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Repeat Frequency</label>
                    <div className="grid grid-cols-3 gap-2.5">
                      {[
                        { value: 'daily', label: 'Daily', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                        { value: 'weekly', label: 'Weekly', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                        { value: 'monthly', label: 'Monthly', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' }
                      ].map(freq => (
                        <button
                          key={freq.value}
                          type="button"
                          onClick={() => setRepeatFrequency(freq.value)}
                          className={`py-2 px-3 sm:py-3 sm:px-4 rounded-xl border transition-all text-xs sm:text-sm font-semibold flex flex-col items-center justify-center ${repeatFrequency === freq.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-xs'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-200 hover:bg-indigo-50/30'
                            }`}
                        >
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1 sm:mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={freq.icon}></path>
                          </svg>
                          {freq.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Daily Explanation */}
                  {repeatFrequency === 'daily' && (
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5 px-1">
                      <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>A new task will be created daily between {recurringStartDate ? new Date(recurringStartDate).toLocaleDateString('en-GB') : 'start date'} and {recurringEndDate ? new Date(recurringEndDate).toLocaleDateString('en-GB') : 'end date'}.</span>
                    </p>
                  )}

                  {/* Weekly Selection */}
                  {repeatFrequency === 'weekly' && (
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600">
                        Select Weekdays <span className="text-xs font-normal text-gray-400">({repeatWeekdays.length} selected)</span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {weekdays.map(day => {
                          const isSelected = repeatWeekdays.includes(day.value);
                          return (
                            <button
                              key={day.value}
                              type="button"
                              onClick={() => toggleWeekday(day.value)}
                              className={`p-2 rounded-xl border transition-all text-xs sm:text-sm font-semibold flex items-center justify-between ${isSelected
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-200'
                                }`}
                            >
                              <span>{day.label}</span>
                              {isSelected && (
                                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                              )}
                            </button>
                          )
                        })}
                      </div>
                      {repeatWeekdays.length > 0 && recurringStartDate && recurringEndDate && (
                        <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5 px-1">
                          <svg className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <span>Will repeat on <strong>{repeatWeekdays.map(day => weekdays.find(d => d.value === day)?.label).join(', ')}</strong> between {new Date(recurringStartDate).toLocaleDateString('en-GB')} and {new Date(recurringEndDate).toLocaleDateString('en-GB')}.</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Monthly Selection */}
                  {repeatFrequency === 'monthly' && (
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600">
                        Select Days of Month <span className="text-xs font-normal text-gray-400">({repeatMonthDays.length} selected)</span>
                      </label>
                      <div className="grid grid-cols-7 gap-1.5 max-h-36 overflow-y-auto p-2 border border-gray-200 rounded-xl bg-white">
                        {monthDays.map(day => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleMonthDay(day)}
                            className={`p-1 rounded-lg border transition-all text-xs sm:text-sm font-semibold flex items-center justify-center h-8 sm:h-9 ${repeatMonthDays.includes(day)
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-200'
                              }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                      {repeatMonthDays.length > 0 && recurringStartDate && recurringEndDate && (
                        <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5 px-1">
                          <svg className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <span>Will repeat on days <strong>{repeatMonthDays.sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}</strong> of each month between {new Date(recurringStartDate).toLocaleDateString('en-GB')} and {new Date(recurringEndDate).toLocaleDateString('en-GB')}.</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CONDITIONAL: Schedule & Timeline Section - HIDDEN when recurring is enabled */}
            {!repeat && (
              <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  Schedule & Timeline
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {/* Start Date */}
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Start Date & Time <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-row gap-2">
                      <div className="relative flex-[3]">
                        <input
                          type="date"
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white ${formErrors.startDate ? 'border-red-500' : 'border-gray-300'
                            } ${isHoliday(startDate) ? 'bg-red-50 border-red-300' : ''}`}
                          value={startDate}
                          min={today}
                          onChange={(e) => handleStartDateChange(e.target.value)}
                          required={!repeat}
                        />
                      </div>
                      <div className="relative flex-[2]">
                        <input
                          type="time"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                        />
                      </div>
                    </div>
                    {isHoliday(startDate) && (
                      <div className="mt-1 text-xs text-red-600 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                        </svg>
                        This date is a holiday. It will be automatically shifted to the previous working day.
                      </div>
                    )}
                    {selectedEmployees.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Auto-filled from employee shift (editable)
                      </p>
                    )}

                    {formErrors.startDate && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                        </svg>
                        {formErrors.startDate}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-row gap-2">
                    <div className="relative flex-[3]">
                      <input
                        type="date"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white ${formErrors.deadline ? 'border-red-500' : 'border-gray-300'
                          } ${isHoliday(deadline) ? 'bg-red-50 border-red-300' : ''}`}
                        value={deadline}
                        onChange={(e) => handleDeadlineChange(e.target.value)}
                        required={!repeat}
                      />
                    </div>
                    <div className="relative flex-[2]">
                      <input
                        type="time"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                      />
                    </div>
                  </div>
                  {isHoliday(deadline) && (
                    <div className="mt-1 text-xs text-red-600 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                      </svg>
                      This date is a holiday. It will be automatically shifted to the previous working day.
                    </div>
                  )}
                  {selectedEmployees.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Auto-filled from employee shift (editable)
                    </p>
                  )}

                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                    Next Follow-Up Date & Time <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-row gap-2">
                    <div className="relative flex-[3]">
                      <input
                        type="date"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white ${formErrors.nextFollowUpDate ? 'border-red-500' : 'border-gray-300'
                          } ${isHoliday(nextFollowUpDate) ? 'bg-red-50 border-red-300' : ''}`}
                        value={nextFollowUpDate}
                        min={today}
                        onChange={(e) => {
                          setNextFollowUpDate(e.target.value);
                          if (formErrors.nextFollowUpDate) {
                            setFormErrors({ ...formErrors, nextFollowUpDate: '' });
                          }
                        }}
                        required={!repeat}
                      />
                    </div>
                    <div className="relative flex-[2]">
                      <input
                        type="time"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                        value={nextFollowUpTime}
                        onChange={(e) => setNextFollowUpTime(e.target.value)}
                      />
                    </div>
                  </div>
                  {isHoliday(nextFollowUpDate) && (
                    <div className="mt-1 text-xs text-red-600 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                      </svg>
                      This date is a holiday. It will be automatically shifted to the previous working day.
                    </div>
                  )}
                  {selectedEmployees.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Auto-filled from employee shift (editable)
                    </p>
                  )}

                  {formErrors.nextFollowUpDate && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                      </svg>
                      {formErrors.nextFollowUpDate}
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* Attachments Section */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 00-5.656-5.656l-6.586 6.586a6 6 0 108.486 8.486L20.5 13"></path>
                </svg>
                Attachments & Media
              </h3>

              <div className="grid grid-cols-3 gap-2 sm:grid-cols-3 sm:gap-3 mb-3 sm:mb-4">
                <input type="file" ref={imageInputRef} onChange={(e) => handleFileUpload(e, 'image')} accept="image/*" multiple className="hidden" />

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileUpload(e, 'file')}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,application/pdf"
                  multiple
                  className="hidden"
                />

                <input type="file" ref={audioInputRef} onChange={(e) => handleFileUpload(e, 'audio')} accept="audio/*" multiple className="hidden" />

                <button
                  type="button"
                  onClick={() => imageInputRef.current.click()}
                  className="flex flex-col items-center p-2.5 sm:p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg sm:rounded-xl transition-colors"
                >
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-blue-700">Images</span>
                  <span className="text-[10px] sm:text-xs text-blue-600">Max 5 MB</span>
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="flex flex-col items-center p-2.5 sm:p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg sm:rounded-xl transition-colors"
                >
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-purple-700">Files</span>
                  <span className="text-[10px] sm:text-xs text-purple-600">Max 5 MB</span>
                </button>

                <button
                  type="button"
                  onClick={() => audioInputRef.current.click()}
                  className="flex flex-col items-center p-2.5 sm:p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg sm:rounded-xl transition-colors"
                >
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-500 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path>
                    </svg>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-green-700">Audio Files</span>
                  <span className="text-[10px] sm:text-xs text-green-600">Max 5 MB</span>
                </button>
              </div>

              {attachments.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center text-sm">
                    Attached Files ({attachments.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {attachments.map(attachment => (
                      <div key={attachment.id} className="flex items-center justify-between p-3 bg-white rounded border border-gray-200 hover:shadow-sm transition-shadow">
                        <div className="flex items-center flex-1 min-w-0">
                          {/* UPDATED: Conditional preview based on type */}
                          {attachment.type === 'image' && attachment.url ? (
                            <div className="relative mr-3">
                              <img
                                src={attachment.url}
                                alt={attachment.name}
                                className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                                onError={(e) => {
                                  e.target.style.display = 'none';  // Hide broken image
                                  e.target.nextSibling.style.display = 'flex';  // Show fallback icon
                                }}
                              />
                              {/* Fallback icon if image fails */}
                              <div className="absolute inset-0 w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center hidden">
                                {getFileIcon('image')}
                              </div>
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
                              {getFileIcon(attachment.type)}
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 truncate text-sm mb-1">
                              {attachment.name || `${attachment.type}_file`}
                            </p>
                            {attachment.size > 0 && (
                              <p className="text-xs text-gray-500">
                                {(attachment.size / 1024).toFixed(1)} KB
                              </p>
                            )}
                            {/* Debug info - remove after testing */}
                            <p className="text-xs text-gray-400 truncate">
                              Type: {attachment.type}
                            </p>
                          </div>
                        </div>

                        {/* UPDATED: Action buttons based on type */}
                        <div className="flex items-center space-x-2 ml-4">
                          {attachment.type === 'image' && attachment.url && (
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                              title="View full image"
                            >
                              View
                            </a>
                          )}
                          {attachment.type === 'file' && attachment.url && (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  console.log('¥ Downloading file:', { name: attachment.name, url: attachment.url });

                                  // Ensure the filename has an extension
                                  let filename = attachment.name || 'download';
                                  if (!filename.includes('.')) {
                                    // Try to get extension from URL
                                    const ext = attachment.url.split('.').pop()?.split('?')[0];
                                    if (ext && ext.length <= 5) {
                                      filename = `${filename}.${ext}`;
                                    }
                                  }

                                  console.log(' Download filename:', filename);

                                  // Use backend proxy API to download file (fixes CORS and content-type issues)
                                  const downloadUrl = `${API_BASE_URL}/files/download?fileUrl=${encodeURIComponent(attachment.url)}`;

                                  const response = await fetch(downloadUrl, {
                                    method: 'GET',
                                    headers: {
                                      'Accept': '*/*'
                                    }
                                  });

                                  if (!response.ok) {
                                    throw new Error(`Download failed: ${response.status}`);
                                  }

                                  const blob = await response.blob();
                                  console.log('📦 Blob created:', { type: blob.type, size: blob.size });

                                  // Create download link
                                  const blobUrl = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.style.display = 'none';
                                  link.href = blobUrl;
                                  link.download = filename;

                                  document.body.appendChild(link);
                                  link.click();

                                  // Cleanup
                                  setTimeout(() => {
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(blobUrl);
                                  }, 100);

                                  console.log('✅ File download initiated:', filename);
                                } catch (error) {
                                  console.error('❌ Download failed:', error);
                                  alert('Failed to download file. Please try again.');
                                }
                              }}
                              className="px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
                              title={`Download ${attachment.name}`}
                            >
                              Download
                            </button>
                          )}
                          {attachment.type === 'audio' && attachment.url && (
                            <button
                              type="button"
                              onClick={() => isPlaying ? pauseAudio() : playAudio(attachment.url)}
                              className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center text-white transition-colors ml-1"
                              title="Play audio"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isPlaying ? "M10 9v6m4-6v6" : "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"}></path>
                              </svg>
                            </button>
                          )}
                          <div className={attachment.type === 'audio' ? "text-xs text-gray-500 min-w-0 ml-1" : "hidden"}>
                            <div>{formatTime(currentTime)} / {formatTime(duration)}</div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeAttachment(attachment.id)}
                            className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors ml-1"
                            title="Remove attachment"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-6 6-6-6m12 0l-6 6-6-6"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 sm:px-6 sm:py-3 text-sm text-gray-700 hover:text-gray-900 font-medium rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 sm:px-6 sm:py-3 text-sm bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center"
              >
                {isSubmitting ? (
                  <React.Fragment>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {initialData ? 'Updating...' : 'Creating...'}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={initialData ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" : "M12 6v6m0 0v6m0-6h6m-6 0H6"}></path>
                    </svg>
                    {initialData ? 'Update' : 'Create'}
                  </React.Fragment>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} className="hidden" />

      {/* Warning Modal - Removed, now using ConfirmDialog */}

      {/* Snackbar Component */}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        duration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        position="top-right"
      />

      {/* Confirm Dialog Component */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={dialogLoading}
        showIcon={true}
      />
    </div>
  );
};

export default TaskForm;


